import io from 'socket.io-client';
import { createSocketAuthRecovery } from './socketAuthRecovery';
import { AppState, Platform } from 'react-native';
import {
  SOCKET_HOST,
  STREAM_STALL_TIMEOUT_MS,
  THINKING_STALL_TIMEOUT_MS,
  PREPROCESSING_STALL_TIMEOUT_MS,
  MAX_STALL_RECOVERY_ATTEMPTS,
  MAX_INFERENCE_IDLE_BEFORE_FORCE_FINISH_MS,
  RECOVERY_STATUS_TRUST_WINDOW_MS,
  RESPONSE_ACTION_TYPES,
} from '@/constants/config';
import getRelativeTime from '@/constants/utils/timestamp';
import * as Crypto from 'expo-crypto';
import { findParentUserMessage, getMessageDisplayInfo, mapSerializedMessageToMessage, messageHasImageOutput, messageHasMediaOutput, isAwaitingRequestedMedia, parametersForOptimisticUserMessage, mergeUserMessageParameters, pickAuthoritativeMessageText, extractMessageModelIds } from '@/constants/utils/message';
import { getClientInfo, trackWhopFirstPrompt } from '@/constants/utils/analytics';
import { trackActivationOnce, ACTIVATION_ACTION } from '@/constants/utils/analyticsEvents';
import { getUserTimezone } from '@/constants/utils/countryDetection';
import { devLog, devWarn } from '@/lib/devLog';
import { getTranslation } from '@/hooks/useTranslation';
import crossTab from '@/lib/crossTab';
import { classifyResponseError } from '@/constants/utils/response_errors';
import { openModelAccessPaywall } from '@/constants/utils/trial';
import { isWorkActive } from '@/constants/work';
import { signalWorkTabAttention } from '@/lib/workTabAttention';
import {
  writeStreamingDraft,
  clearStreamingDraft,
  clearStreamingDraftsForParent,
  flushOpenStreamingDrafts,
} from '@/lib/streamingDraft';

const petReplyCompletedListeners = new Set();

export function subscribePetReplyCompleted(listener) {
  petReplyCompletedListeners.add(listener);
  return () => petReplyCompletedListeners.delete(listener);
}

function notifyPetReplyCompleted(conversationId) {
  if (!conversationId) return;
  petReplyCompletedListeners.forEach(listener => {
    try {
      listener(conversationId);
    } catch (error) {
      console.error('Desktop pet reply listener failed:', error);
    }
  });
}

// When the OS suspends a backgrounded app it silently tears down the socket's
// underlying transport, but socket.io keeps reporting `connected: true` until
// its heartbeat times out (~30-45s). If we were backgrounded longer than this
// threshold we treat a still-"connected" socket as a zombie on resume and force
// a clean reconnect so the UI doesn't appear frozen while every chat
// interaction silently hangs waiting on a dead connection.
//
// Tuned to align with engine.io's heartbeat window: brief backgrounds (incoming
// notification peek, control-center drop-down, tab switch, Copy/Share sheet)
// are almost always shorter than this and leave the transport alive, so we
// avoid needless disconnect/connect churn that briefly reports the socket as
// offline and triggers spurious "check your internet connection" toasts.
// Anything past the heartbeat window is likely a zombie and is forced to
// reconnect. If `socket.connected === false` we already short-circuit above
// and kick an immediate reconnect regardless of duration.
const STALE_SOCKET_RECONNECT_THRESHOLD_MS = 30000;

// Grace window requireConnection() gives an in-flight/pending reconnect before
// blaming the user's network. The socket is routinely down *by design* for
// 1-3s: pause-on-hidden teardown, post-background zombie resync, blue/green
// deploys, backoff between attempts. A send landing inside one of those
// windows used to instantly toast "check your internet connection" even
// though the network was fine and the socket would have reconnected a second
// later. 5s is long enough to cover a polling handshake + token refresh
// round-trip, short enough that a genuinely dead connection still errors
// promptly.
const REQUIRE_CONNECTION_WAIT_MS = 5000;
const REQUIRE_CONNECTION_POLL_MS = 250;
// Show the "Reconnecting…" info toast only if we're STILL down after this
// long, so sub-second blips produce no UI at all.
const REQUIRE_CONNECTION_INFO_TOAST_AFTER_MS = 1500;

// Lazy imports to avoid circular dependency with store/index.js
// The store imports this file, and this file needs the stores
// We use require() instead of import to defer loading until runtime
const useChatStore = { getState: () => require('@/store').useChatStore.getState() };
const useAuthStore = { getState: () => require('@/store').useAuthStore.getState() };
const useToastStore = { getState: () => require('@/store').useToastStore.getState() };
const useFileStore = { getState: () => require('@/store').useFileStore.getState() };
const useMemoryStore = { getState: () => require('@/store').useMemoryStore.getState() };
const useVoiceModeStore = { getState: () => require('@/store').useVoiceModeStore.getState() };
const useChatInputStore = { getState: () => require('@/store').useChatInputStore.getState() };
const useModalStore = { getState: () => require('@/store').useModalStore.getState() };
const useRouteStore = { getState: () => require('@/store').useRouteStore.getState() };
const useCoworkerStore = { getState: () => require('@/store').useCoworkerStore.getState() };
const chatsApi = {
  getConversationById: (...args) => require('@/lib/api').chatsApi.getConversationById(...args),
  getConversationStatus: (...args) => require('@/lib/api').chatsApi.getConversationStatus(...args),
};
// Sanitize chat titles that may arrive as JSON objects or JSON strings from the LLM
// e.g., { title: "Guide to..." } or '{"title":"Guide to..."}'
function _sanitizeChatTitle(title) {
  if (!title) return 'Untitled Chat';
  // Already a clean string
  if (typeof title === 'string') {
    // Check if the string is a JSON object like '{"title":"actual title"}'
    const trimmed = title.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed.title && typeof parsed.title === 'string') {
          return parsed.title.trim();
        }
      } catch (e) {
        // Not valid JSON, return as-is
      }
    }
    return title;
  }
  // Title came as an object (e.g., { title: "actual title" })
  if (typeof title === 'object' && title !== null) {
    if (title.title && typeof title.title === 'string') {
      return title.title.trim();
    }
    // Try to get any string value from the object
    const values = Object.values(title);
    const stringVal = values.find(v => typeof v === 'string');
    if (stringVal) return stringVal.trim();
    return 'Untitled Chat';
  }
  return String(title);
}

class SocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.lastToken = null;
    this.userId = null;

    // Token buffer to throttle React state updates during high-speed streaming
    this.tokenStreamBuffer = new Map();
    this.tokenStreamInterval = null;

    // Deferred socket reconnect: set when the access token was refreshed
    // mid-stream and we're waiting for `conversation_stream_end` to reconnect
    // without killing the active stream. The fallback timer guarantees we
    // reconnect even if stream_end is missed (network blip, service worker
    // interception, orchestrator crash) — without it the socket would stay
    // stranded on the expired token and every subsequent send would fail.
    this._pendingReconnect = false;
    this._deferredReconnectTimer = null;
    this._deferredReconnectToken = null;
    this._deferredReconnectUserId = null;

    // HTTP 429 backoff: when the handshake is rejected with 429 (our own
    // handshake limiter now delays instead, but Cloudflare / upstream proxies
    // / future changes could still 429), we stash the wall-clock time we're
    // allowed to retry at and bump socket.io's reconnectionDelay so its
    // auto-reconnect doesn't keep hammering the endpoint that just told us
    // to slow down. requireConnection() reads this so we show "reconnecting"
    // instead of the misleading "check your internet connection" toast.
    this._rateLimitedUntilMs = 0;
    this._origReconnectionDelay = null;
    
    // Mobile-specific: Track stream end timestamps to ignore stale events
    // Key: conversationId, Value: timestamp when stream ended
    this.streamEndTimestamps = new Map();

    // Track stream activity and watchdog timers (mobile web stall recovery)
    this.streamActivityTimestamps = new Map();
    this.streamWatchdogTimers = new Map();
    // Track which conversations have started streaming content (tokens received)
    // Watchdog only activates AFTER streaming starts, not during preprocessing
    this.streamingStarted = new Map();

    // Socket reconnects can replay a task's terminal update, and billing may
    // emit a second terminal payload with corrected credits. Keep applying the
    // data, but notify the user only once for each task/status pair.
    this._notifiedWorkSettlements = new Set();

    // Track preprocessing watchdog timers (separate from stream watchdog)
    // These detect stalls during the preprocessing/thinking phase before tokens flow
    this.preprocessingWatchdogTimers = new Map();

    // Track consecutive recovery attempts where backend reports still inferencing
    // Used to force-finish after MAX_STALL_RECOVERY_ATTEMPTS to prevent infinite loops
    this.stallRecoveryAttempts = new Map();

    // Track recently rejected conversations (e.g. content moderation)
    // Used to prevent late-arriving events (conversation_stream_start, inference_status)
    // from re-enabling loading state after an error has already cleared it.
    // Key: conversationId, Value: timestamp of rejection
    this.rejectedConversations = new Map();

    // Preserve the visible partial response while a continuation request is
    // starting, so an error does not make the user's generated text disappear.
    this.pendingContinuationMessages = new Map();

    // Offline send queue: conversationId → { userId, conversationId,
    // formattedMessage, optimisticMessageId, queuedAt }. Filled by
    // _queueOfflineSend when a send happens while disconnected; flushed
    // through the normal send path on the next socket connect.
    this.pendingSendQueue = new Map();

    // New-chat kicks that couldn't leave the client (offline or a bailed
    // emit). conversationId → { userId, attempts }. Retried with backoff and
    // flushed on connect so a fresh chat never sits idle after creation.
    this.pendingKicks = new Map();
    this.kickRetryTimers = new Map();
    
    // Mobile-specific: Track app state for handling background/foreground transitions
    this.appState = AppState.currentState;
    this.appStateSubscription = null;
    this.visibilityHandler = null;
    // Timestamp of when the app most recently entered the background, used on
    // resume to decide whether the socket transport has likely gone stale.
    this.backgroundedAt = null;
    this.onlineHandler = null;

    // Pause-on-hidden (web): when a tab is hidden and no chat is actively
    // streaming, the socket is torn down to stop engine.io heartbeats from
    // hammering the backend N×25s at a time across N idle tabs. The next
    // `visibilitychange → visible` reconnects.
    this._pausedDueToHidden = false;
    // Cross-tab dedup of `_softSyncChatList`. Updated locally when this tab
    // syncs and remotely when another tab broadcasts that it just synced.
    this._lastGlobalChatSyncAt = 0;
    this._crossTabChatSyncUnsub = null;
    
    // Set up AppState listener for mobile
    if (Platform.OS !== 'web') {
      this._setupAppStateListener();
    } else {
      this._setupVisibilityListener();
    }
  }

  _restorePendingContinuation(conversationId) {
    if (!conversationId || !this.pendingContinuationMessages.has(conversationId)) return false;

    const pending = this.pendingContinuationMessages.get(conversationId);
    this.pendingContinuationMessages.delete(conversationId);

    try {
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      const message = pending?.message;
      const messageId = message?.id || message?._id || pending?.messageId;
      const alreadyExists = chat?.messages?.some(m =>
        (messageId && (m.id === messageId || m._id === messageId)) ||
        (message?.content && m.role === message.role && m.content === message.content)
      );

      if (message && !alreadyExists) {
        chatStore.addMessage(conversationId, {
          ...message,
          id: messageId,
          isThinking: false,
          isTyping: false,
          isWaiting: false,
          isStreaming: false,
        });
      } else if (message && alreadyExists && messageId) {
        chatStore.updateLocalChatMessage(conversationId, messageId, {
          ...message,
          isThinking: false,
          isTyping: false,
          isWaiting: false,
          isStreaming: false,
        });
      }

      return true;
    } catch (error) {
      console.error('[Socket] Failed to restore pending continuation:', error);
      return false;
    }
  }
  
  // Handle app state changes (background/foreground) on mobile
  _setupAppStateListener() {
    this.appStateSubscription = AppState.addEventListener('change', (nextAppState) => {
      const prevState = this.appState;
      this.appState = nextAppState;
      
      devLog('[Socket] AppState changed:', { from: prevState, to: nextAppState });
      
      if (prevState === 'active' && nextAppState.match(/inactive|background/)) {
        try {
          flushOpenStreamingDrafts(useChatStore.getState().chats);
        } catch (_) { /* best effort */ }
      }

      // App coming back to foreground
      if (prevState.match(/inactive|background/) && nextAppState === 'active') {
        devLog('[Socket] App returned to foreground, cleaning up stale streaming states');
        // Re-establish the socket BEFORE running recovery. iOS reaps the
        // connection while suspended without socket.io noticing, so resuming
        // sessions would otherwise hang until the heartbeat eventually times
        // out (the user-visible "app freezes after switching back" symptom).
        this._resyncSocketOnForeground();
        this._cleanupStaleStreamingStates();
        this._recoverStalledStreamsOnResume();
        // Sync the conversation list to pick up creates/deletes made on other
        // devices while backgrounded (the native counterpart of the web
        // visibilitychange soft sync). HTTP-based, so it doesn't need to wait
        // for the socket reconnect above to complete.
        this._softSyncChatList({ force: true });
      }

      // App going to background - record when so we can detect a stale
      // (zombie) socket on the next resume. Only capture the first transition
      // into background/inactive, not repeated events while already there.
      if (nextAppState.match(/inactive|background/) && !prevState.match(/inactive|background/)) {
        this.backgroundedAt = Date.now();
        devLog('[Socket] App going to background');
      }
    });
  }

  // Reconnect the socket if it likely went stale while the app was suspended.
  // iOS keeps the JS `socket.connected` flag true across suspension even though
  // the OS already killed the underlying transport, so that flag alone can't be
  // trusted on resume. If socket.io already knows it's down, kick an immediate
  // reconnect instead of waiting out the backoff timer; if it still *thinks*
  // it's up but we were backgrounded long enough for the transport to have been
  // reaped, force a clean reconnect rather than waiting for engine.io's
  // heartbeat timeout (~30-45s) to discover the dead connection.
  _resyncSocketOnForeground() {
    if (!this.socket) return;

    const backgroundedMs = this.backgroundedAt ? Date.now() - this.backgroundedAt : null;
    this.backgroundedAt = null;

    try {
      if (!this.socket.connected) {
        devLog('[Socket] Foreground resync: socket reported disconnected, reconnecting now');
        this.isConnected = false;
        this.socket.connect();
        return;
      }

      // Socket claims to be connected. A brief task-switch almost certainly
      // left the transport alive, so leave it untouched to avoid needless
      // connection churn. Past the threshold, assume it's a zombie and force a
      // fresh connection so the resumed session is responsive immediately. If
      // we never recorded a background timestamp (backgroundedMs == null) we
      // can't have been suspended long enough to go stale, so leave the live
      // connection alone — this guards against a future listener refactor
      // accidentally forcing a reconnect on a spurious foreground event.
      if (backgroundedMs != null && backgroundedMs >= STALE_SOCKET_RECONNECT_THRESHOLD_MS) {
        devLog('[Socket] Foreground resync: forcing reconnect after background', { backgroundedMs });
        this.isConnected = false;
        this.socket.disconnect();
        this.socket.connect();
      }
    } catch (e) {
      devWarn('[Socket] Foreground resync failed:', e?.message || e);
    }
  }

  // Soft-sync the conversation list (merge + prune deletions), rate-limited so
  // rapid foreground/reconnect churn doesn't hammer the API. Best-effort.
  // The throttle is applied across BOTH dimensions:
  //   1. Per-tab (`_lastChatListSyncAt`) — the same tab won't spam.
  //   2. Cross-tab (`_lastGlobalChatSyncAt`) — when the user clicks between
  //      several open tabs, only the first one in any 30s window actually
  //      hits the API. Each tab broadcasts its sync timestamp so the others
  //      can short-circuit. This caps focus-burst load at one sync per window
  //      regardless of how many tabs regain focus in quick succession.
  _softSyncChatList({ force = false } = {}) {
    const now = Date.now();
    // Native foreground is the moment we learn about deletes made on
    // desktop while the phone was suspended — don't let the tab-focus
    // 30s throttle skip that catch-up. A short floor still absorbs iOS
    // inactive↔active flicker (Control Center, notification shade).
    const minInterval = force ? 5_000 : 30_000;
    if (this._lastChatListSyncAt && now - this._lastChatListSyncAt < minInterval) return;
    if (!force && this._lastGlobalChatSyncAt && now - this._lastGlobalChatSyncAt < 30_000) return;
    this._lastChatListSyncAt = now;
    try {
      const chatStore = useChatStore.getState();
      if (chatStore.softSyncChats) {
        chatStore.softSyncChats({ force });
      }
      // Announce to other tabs so they skip their own sync within the window.
      if (crossTab) crossTab.send('chat-sync', { ts: now });
    } catch (e) {
      devWarn('[Socket] Chat list soft sync failed:', e?.message || e);
    }
  }

  _setupVisibilityListener() {
    // Only set up visibility listener on web - native uses AppState instead
    if (Platform.OS !== 'web' || typeof document === 'undefined' || this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        // Resume a socket that we paused when the tab went hidden.
        if (this._pausedDueToHidden) {
          this._resumeAfterHidden();
        }
        devLog('[Socket] Page visible, checking for stale streams');
        // A tab can remain disconnected after a long outage or a failed
        // WebSocket handshake. Kick the existing Socket.IO manager so users
        // do not have to reload the page to resume chatting.
        this._resyncSocketOnForeground();
        this._cleanupStaleStreamingStates();
        this._recoverStalledStreamsOnResume();

        // Soft-sync the conversation list to pick up any changes
        // made in other tabs while this tab was hidden (skip if not connected)
        if (this.isConnected) {
          this._softSyncChatList();
        }
      } else if (document.visibilityState === 'hidden') {
        try {
          flushOpenStreamingDrafts(useChatStore.getState().chats);
        } catch (_) { /* best effort */ }
        // Idle hidden tabs are the dominant source of unnecessary backend
        // load: each one keeps a Socket.IO connection alive and exchanges
        // engine.io ping/pong frames every ~25s forever. If nothing is
        // actively streaming, tear the connection down now; we'll reconnect
        // on the next `visibilitychange → visible`. Active streams are left
        // alone so we don't drop tokens mid-generation (the user may still
        // be watching in a side-by-side window).
        this._maybePauseWhenHidden();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Browser `online` is only a hint that the network is available, but it is
    // a useful trigger for an immediate attempt instead of waiting for the
    // current exponential-backoff delay.
    if (typeof window !== 'undefined') {
      this.onlineHandler = () => this._resyncSocketOnForeground();
      window.addEventListener('online', this.onlineHandler);
    }

    // Subscribe to other tabs' chat-list sync announcements so we can skip
    // our own redundant sync within the throttle window.
    if (crossTab && !this._crossTabChatSyncUnsub) {
      this._crossTabChatSyncUnsub = crossTab.onMessage('chat-sync', ({ ts }) => {
        if (typeof ts === 'number' && ts > this._lastGlobalChatSyncAt) {
          this._lastGlobalChatSyncAt = ts;
        }
      });
    }
  }

  // True if any conversation is actively producing tokens or in the
  // pre-streaming Thinking/MessageWaiting phase. Used to decide whether it's
  // safe to pause the socket on tab hide.
  _hasActiveStreams() {
    if (this.streamingStarted && this.streamingStarted.size > 0) return true;
    try {
      const chatStore = useChatStore.getState();
      return chatStore.chats.some(
        (c) => c && (c.isInferencing || c.isInferencingWaiting),
      );
    } catch (_) {
      return false;
    }
  }

  // Tear down the socket when the tab goes hidden to stop per-tab engine.io
  // heartbeats from running up the backend bill on idle background tabs.
  // Skipped if any chat is mid-generation so we don't corrupt the response.
  _maybePauseWhenHidden() {
    if (!this.socket || !this.isConnected) return;
    if (this._hasActiveStreams()) {
      devLog('[Socket] Tab hidden but active streams in flight — keeping connection');
      return;
    }
    devLog('[Socket] Tab hidden with no active streams — pausing socket');
    this._pausedDueToHidden = true;
    try {
      if (this.userId) {
        // Emit unsubscribe so the backend stops queuing user-channel events
        // for this connection; the resume path re-subscribes on connect.
        this.socket.emit('unsubscribe', { userId: this.userId });
      }
      this.socket.disconnect();
      this.isConnected = false;
    } catch (e) {
      devWarn('[Socket] Pause-on-hidden failed:', e?.message || e);
      this._pausedDueToHidden = false;
    }
  }

  // Reconnect a socket that was paused by `_maybePauseWhenHidden`.
  _resumeAfterHidden() {
    this._pausedDueToHidden = false;
    if (!this.lastToken || !this.userId) return;
    devLog('[Socket] Tab visible again — resuming socket');
    try {
      if (this.socket) {
        // socket.io connect() is idempotent on an already-disconnected socket
        // and re-runs the auth handshake with the cached `auth` payload.
        this.socket.connect();
      } else {
        this.connect(this.lastToken, this.userId);
      }
    } catch (e) {
      devWarn('[Socket] Resume-after-hidden failed:', e?.message || e);
    }
  }
  
  // Clean up any messages stuck in streaming state when app returns to foreground
  _cleanupStaleStreamingStates() {
    try {
      const chatStore = useChatStore.getState();
      let cleanedCount = 0;
      // On native, use a short threshold so recovery kicks in quickly when the
      // user returns from background. On web, keep the longer threshold since
      // the tab may have been hidden but events still arrived.
      const STALE_THRESHOLD_MS = Platform.OS === 'web' ? 2 * 60 * 1000 : 10 * 1000;
      
      chatStore.chats.forEach(chat => {
        // Find all messages that appear to be stuck streaming
        const stuckMessages = chat?.messages?.filter(m => 
          m.role === 'assistant' && 
          (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
        ) || [];
        
        if (stuckMessages.length > 0 && !chat.isInferencing) {
          // Chat is not inferencing but has streaming messages - they're stuck
          stuckMessages.forEach(msg => {
            chatStore.updateLocalChatMessage(chat.id, msg.id, {
              isThinking: false,
              isTyping: false,
              isWaiting: false,
              isStreaming: false,
              inferenceStatus: undefined,
              inferenceStage: undefined,
              inferenceStatusTimestamp: undefined
            });
            cleanedCount++;
          });
          
          // Also ensure the chat's inferencing state is cleared
          chatStore.updateLocalChatDetails(chat.id, {
            isInferencing: false,
            isInferencingWaiting: false,
          });
        } else if (chat.isInferencing || chat.isInferencingWaiting) {
          // Still marked as generating — including the early Thinking /
          // MessageWaiting phase where no assistant placeholder exists yet
          // (stuckMessages.length === 0). Check backend and hydrate if the
          // stream finished while the app was backgrounded.
          const lastActivity = this.streamActivityTimestamps.get(chat.id);
          const now = Date.now();
          const timeSinceLastActivity = lastActivity ? now - lastActivity : null;
          
          // If no activity timestamp or activity is stale, trigger recovery
          // This handles the case where user left during "thinking" phase before streaming started
          if (!lastActivity || timeSinceLastActivity > STALE_THRESHOLD_MS) {
            devLog('[Socket] Found stale inferencing chat on foreground resume', {
              conversationId: chat.id,
              timeSinceLastActivity,
              staleThreshold: STALE_THRESHOLD_MS,
              stuckMessagesCount: stuckMessages.length,
              isInferencingWaiting: !!chat.isInferencingWaiting,
            });
            // Use recovery function to check with backend and clean up if needed
            this._recoverStalledConversation(chat.id, 'foreground_resume');
          } else if (stuckMessages.length > 0) {
            // Activity is recent, just restart the watchdog to continue monitoring
            devLog('[Socket] Inferencing chat has recent activity, restarting watchdog', {
              conversationId: chat.id,
              timeSinceLastActivity,
            });
            this._startStreamWatchdog(chat.id);
          } else {
            this._startPreprocessingWatchdog(chat.id);
          }
        }
      });
      
      if (cleanedCount > 0) {
        devLog('[Socket] Cleaned up', cleanedCount, 'stuck streaming messages after foreground');
      }
    } catch (e) {
      console.error('[Socket] Error cleaning up stale streaming states:', e);
    }
  }

  _recoverStalledStreamsOnResume() {
    const chatStore = useChatStore.getState();
    chatStore.chats.forEach(chat => {
      // Include early Thinking / MessageWaiting (isInferencingWaiting) even if
      // isInferencing was somehow cleared — otherwise a backgrounded Thinking
      // turn never hydrates on resume.
      if (!chat?.isInferencing && !chat?.isInferencingWaiting) return;
      
      const lastActivity = this.streamActivityTimestamps.get(chat.id);
      const timeSinceLastActivity = lastActivity ? Date.now() - lastActivity : null;
      
      devLog('[Socket] Checking stream on visibility resume', {
        conversationId: chat.id,
        timeSinceLastActivity,
        isSocketConnected: this.isConnected,
        staleThreshold: STREAM_STALL_TIMEOUT_MS,
      });
      
      if (this.isConnected) {
        // On native, if there's been no activity for more than a few seconds,
        // immediately check with the backend instead of waiting for the full
        // watchdog timeout. The server likely finished while the app was
        // backgrounded and socket events were lost.
        const quickRecoverThreshold = Platform.OS === 'web' ? STREAM_STALL_TIMEOUT_MS : 5000;
        if (!lastActivity || (timeSinceLastActivity && timeSinceLastActivity > quickRecoverThreshold)) {
          devLog('[Socket] Triggering immediate recovery on resume (no recent activity)', {
            conversationId: chat.id,
            timeSinceLastActivity,
          });
          this._recoverStalledConversation(chat.id, 'foreground_resume_immediate');
        } else if (this.streamingStarted.get(chat.id)) {
          this._startStreamWatchdog(chat.id);
        } else {
          this._startPreprocessingWatchdog(chat.id);
        }
      } else {
        // Socket disconnected - wait for reconnection, don't clear state
        devLog('[Socket] Socket disconnected on resume, waiting for reconnect');
      }
    });
  }

  _clearStreamWatchdog(conversationId) {
    const timer = this.streamWatchdogTimers.get(conversationId);
    if (timer) {
      clearTimeout(timer);
      this.streamWatchdogTimers.delete(conversationId);
    }
  }

  // Start a preprocessing-specific watchdog that detects stalls before tokens flow.
  // Uses a longer timeout than the stream watchdog since preprocessing large contexts
  // (books, long conversations) naturally takes longer between heartbeats.
  _startPreprocessingWatchdog(conversationId) {
    if (!conversationId) return;
    // Don't start if streaming has already started (stream watchdog handles that)
    if (this.streamingStarted.get(conversationId)) return;

    this._clearPreprocessingWatchdog(conversationId);

    const timer = setTimeout(async () => {
      // If streaming started in the meantime, let the stream watchdog handle it
      if (this.streamingStarted.get(conversationId)) return;

      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      const lastActivity = this.streamActivityTimestamps.get(conversationId);
      const now = Date.now();

      if (!chat || !chat.isInferencing) {
        this._clearPreprocessingWatchdog(conversationId);
        return;
      }

      // If we've had recent activity, restart the timer
      if (lastActivity && (now - lastActivity) < PREPROCESSING_STALL_TIMEOUT_MS) {
        this._startPreprocessingWatchdog(conversationId);
        return;
      }

      devLog('[Socket] Preprocessing watchdog triggered — no heartbeat received', {
        conversationId,
        timeSinceLastActivity: lastActivity ? now - lastActivity : 'never',
      });

      this._recoverStalledConversation(conversationId, 'preprocessing_watchdog');
    }, PREPROCESSING_STALL_TIMEOUT_MS);

    this.preprocessingWatchdogTimers.set(conversationId, timer);
  }

  _clearPreprocessingWatchdog(conversationId) {
    const timer = this.preprocessingWatchdogTimers.get(conversationId);
    if (timer) {
      clearTimeout(timer);
      this.preprocessingWatchdogTimers.delete(conversationId);
    }
  }

  // Mark activity during preprocessing (heartbeats) and start preprocessing watchdog
  _markPreprocessingActivity(conversationId) {
    if (!conversationId) return;
    this.streamActivityTimestamps.set(conversationId, Date.now());
    // Start preprocessing watchdog to detect if heartbeats stop arriving
    this._startPreprocessingWatchdog(conversationId);
  }

  // Mark activity AND start watchdog - only call this when actual content is streaming
  _markStreamActivity(conversationId) {
    if (!conversationId) return;
    this.streamActivityTimestamps.set(conversationId, Date.now());
    // Real tokens flowing — reset recovery attempt counter
    this.stallRecoveryAttempts.delete(conversationId);
    // Only start watchdog if streaming has started (tokens are flowing)
    if (this.streamingStarted.get(conversationId)) {
      this._startStreamWatchdog(conversationId);
    }
  }

  // Add an inline moderation error message to the chat (instead of a toast)
  _addModerationMessage(conversationId, errorMessage) {
    const moderationId = `moderation-${Crypto.randomUUID()}`;
    useChatStore.getState().addMessage(conversationId, {
      id: moderationId,
      content: errorMessage || "I can't help with that request.",
      moderationMessage: errorMessage || "I can't help with that request.",
      role: 'assistant',
      isError: true,
      buttons: [{ action: RESPONSE_ACTION_TYPES.CONTENT_MODERATION }],
      timestamp: new Date().toISOString(),
    });
  }

  // Check if an error is a content moderation error based on server-provided
  // actions, reason, or errorCode. Returns true only when the server
  // explicitly indicates content moderation, preventing generic errors (e.g.
  // "Failed to build any valid requests") from being misclassified as
  // moderation on native platforms. The moderation rejection path throws
  // CleanError with errorCode 'CONTENT_MODERATION' — no reason/action — so
  // errorCode is the signal that actually arrives for real rejections.
  _isContentModerationError(actions, reason, errorCode) {
    if (reason === 'CONTENT_MODERATION' || errorCode === 'CONTENT_MODERATION') return true;
    if (Array.isArray(actions) && actions.some(a =>
      a?.action === RESPONSE_ACTION_TYPES.CONTENT_MODERATION ||
      a?.action === 'content-moderation'
    )) return true;
    return false;
  }

  // Check if an error is the backend's email-verification gate
  // (permissions_validator_service USER_EMAIL_VERIFICATION_REQUIRED). The
  // backend attaches a 'resend-verification' action; the message match is a
  // fallback for older payload shapes.
  _isVerifyEmailError(actions, errorMessage) {
    if (Array.isArray(actions) && actions.some(a => a?.action === 'resend-verification')) return true;
    if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('verify your email')) return true;
    return false;
  }

  _isVerifyPhoneError(actions, errorMessage) {
    if (Array.isArray(actions) && actions.some(a => a?.action === 'verify-phone')) return true;
    if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('verify your phone')) return true;
    return false;
  }

  _isReviewHoldError(errorMessage, errorCode) {
    return errorCode === 'account_held_for_review'
      || (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('while it is reviewed'));
  }

  _refreshReviewHoldState(errorMessage, errorCode, source) {
    if (!this._isReviewHoldError(errorMessage, errorCode)) return false;
    try {
      useAuthStore.getState().fetchUserSlim?.();
    } catch (_) { /* best effort */ }
    this._openReviewHoldModal(source || 'socket_error');
    return true;
  }

  _openReviewHoldModal(source) {
    try {
      const { openModal } = useModalStore.getState().actions;
      openModal('AccountReviewHoldModal', { source });
    } catch (e) {
      console.error('[Socket] Failed to open AccountReviewHoldModal:', e);
      try {
        const { addToast } = useToastStore.getState().actions;
        addToast(getTranslation('modals.reviewHold.title'), 'error');
      } catch (_) { /* best effort */ }
    }
  }

  _openVerifyEmailModal(source) {
    try {
      const { openModal } = useModalStore.getState().actions;
      openModal('VerifyEmailModal', { source });
    } catch (e) {
      console.error('[Socket] Failed to open VerifyEmailModal:', e);
      try {
        const { addToast } = useToastStore.getState().actions;
        addToast(getTranslation('modals.verifyEmail.title'), 'error', [
          { title: 'Resend verification email', action: 'resend-verification' },
        ]);
      } catch (_) { /* best effort */ }
    }
  }

  _openVerifyPhoneModal(source) {
    try {
      const { openModal } = useModalStore.getState().actions;
      openModal('VerifyPhoneModal', { source });
    } catch (e) {
      console.error('[Socket] Failed to open VerifyPhoneModal:', e);
      try {
        const { addToast } = useToastStore.getState().actions;
        addToast(getTranslation('modals.verifyPhone.title'), 'error', [
          { title: 'Verify phone number', action: 'verify-phone' },
        ]);
      } catch (_) { /* best effort */ }
    }
  }

  // Record a conversation rejection to prevent late events from re-enabling loading
  _markConversationRejected(conversationId) {
    this.rejectedConversations.set(conversationId, Date.now());
    // Auto-clean after 30 seconds
    setTimeout(() => {
      this.rejectedConversations.delete(conversationId);
    }, 30000);
  }

  // Check if a conversation was recently rejected (within last 10 seconds)
  _isConversationRecentlyRejected(conversationId) {
    const rejectionTime = this.rejectedConversations.get(conversationId);
    if (!rejectionTime) return false;
    return (Date.now() - rejectionTime) < 10000;
  }

  // Call this when first token/content arrives to activate the watchdog
  _markStreamingStarted(conversationId) {
    if (!conversationId) return;
    // Activation is "they saw the product work", which is the first token
    // arriving rather than the send. Recorded once per account, ever.
    trackActivationOnce(
      this.userId || useAuthStore.getState().user?.id,
      ACTIVATION_ACTION.FIRST_RESPONSE_RECEIVED,
    );
    this.streamingStarted.set(conversationId, true);
    this.streamActivityTimestamps.set(conversationId, Date.now());
    // Preprocessing is done — switch from preprocessing watchdog to stream watchdog
    this._clearPreprocessingWatchdog(conversationId);
    this.stallRecoveryAttempts.delete(conversationId);
    this._startStreamWatchdog(conversationId);
    devLog('[Socket] Streaming started, watchdog activated', { conversationId });
  }

  _startTokenStreamFlusher() {
    if (this.tokenStreamInterval) return;
    // Native platforms get a slower flush rate to reduce re-renders and prevent
    // visual glitches (user message flashing, scroll jitter). Web can handle
    // more frequent updates without issue.
    const flushMs = Platform.OS === 'web' ? 50 : 100;
    this.tokenStreamInterval = setInterval(() => {
      this._flushTokenStreamBuffer();
      // Auto-stop the interval when the buffer is empty to avoid idle ticks
      if (this.tokenStreamBuffer.size === 0) {
        clearInterval(this.tokenStreamInterval);
        this.tokenStreamInterval = null;
      }
    }, flushMs);
  }

  _flushTokenStreamBuffer(conversationId = null) {
    if (this.tokenStreamBuffer.size === 0) return;
    
    const chatStore = useChatStore.getState();
    const keysToDelete = [];
    const conversationsToUpdate = new Set();
    
    for (const [key, data] of this.tokenStreamBuffer.entries()) {
      if (!conversationId || data.conversationId === conversationId) {
        conversationsToUpdate.add(data.conversationId);
        // Only clear inference status when leaving the thinking/waiting phase
        // (answer tokens arriving). Clearing on every thinking-token flush races
        // with thinking_keepalive heartbeats ("Thinking...") and falls back to
        // rotating phrases like "Starting your request…" — rapid status flicker.
        const leavingThinkingPhase = !data.isThinking;
        chatStore.updateLocalChatMessage(data.conversationId, data.messageId, {
          ...(data.content !== undefined ? { content: data.content } : {}),
          ...(data.thinkingContent !== undefined ? { thinkingContent: data.thinkingContent } : {}),
          ...(data.thinkingDurationMs !== undefined ? { thinkingDurationMs: data.thinkingDurationMs } : {}),
          isThinking: data.isThinking || false,
          isWaiting: false,
          isStreaming: true,
          isTyping: true,
          isError: false,
          ...(leavingThinkingPhase ? {
            inferenceStatus: undefined,
            inferenceStage: undefined,
            canFastAnswer: false,
          } : {}),
        });
        if (typeof data.content === 'string' && data.content.trim()) {
          writeStreamingDraft({
            conversationId: data.conversationId,
            messageId: data.messageId,
            parentMessageId: data.parentMessageId,
            content: data.content,
            thinkingContent: data.thinkingContent,
          });
        }
        keysToDelete.push(key);
      }
    }
    
    for (const cid of conversationsToUpdate) {
      // updateLocalChatDetails now skips the update when values are already
      // identical, so this is safe to call on every flush without extra checks.
      chatStore.updateLocalChatDetails(cid, {
        isInferencing: true,
        isInferencingWaiting: false
      });
    }
    
    for (const key of keysToDelete) {
      this.tokenStreamBuffer.delete(key);
    }
  }

  // Clear streaming state when stream ends
  _clearStreamingState(conversationId) {
    this._flushTokenStreamBuffer(conversationId);
    this.streamingStarted.delete(conversationId);
    this._clearStreamWatchdog(conversationId);
    this._clearPreprocessingWatchdog(conversationId);
    this.streamActivityTimestamps.delete(conversationId);
    this.stallRecoveryAttempts.delete(conversationId);
  }

  // True while an in-flight assistant message has reasoning/waiting state but
  // no answer text yet. Used to apply the longer thinking stall timeout.
  _isThinkingOnlyStream(conversationId) {
    const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
    if (!chat?.messages?.length) return false;
    const streamingMessage = [...chat.messages].reverse().find(m =>
      m.role === 'assistant' && (m.isThinking || m.isWaiting || m.isStreaming || m.isTyping)
    );
    if (!streamingMessage) return false;
    const hasAnswer =
      typeof streamingMessage.content === 'string' &&
      streamingMessage.content.trim().length > 0;
    return !hasAnswer;
  }

  _getStreamStallTimeoutMs(conversationId) {
    return this._isThinkingOnlyStream(conversationId)
      ? THINKING_STALL_TIMEOUT_MS
      : STREAM_STALL_TIMEOUT_MS;
  }

  // Collapse a stalled/failed thinking-only turn into a visible assistant
  // error (or truncated Continue affordance) instead of an empty bubble.
  // Skips async media placeholders — those have their own error path.
  _finalizeStalledAssistantMessages(conversationId, {
    errorMessage = 'The response stalled before an answer was produced. Please try again.',
    toast = true,
  } = {}) {
    const chatStore = useChatStore.getState();
    const chat = chatStore.chats.find(c => c.id === conversationId);
    const streamingMessages = chat?.messages?.filter(m =>
      m.role === 'assistant' &&
      (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking) &&
      !isAwaitingRequestedMedia(m)
    ) || [];

    streamingMessages.forEach(streamingMessage => {
      const hasContent =
        typeof streamingMessage.content === 'string' &&
        streamingMessage.content.trim().length > 0;
      const hasThinking =
        typeof streamingMessage.thinkingContent === 'string' &&
        streamingMessage.thinkingContent.trim().length > 0;

      chatStore.updateLocalChatMessage(conversationId, streamingMessage.id, {
        isThinking: false,
        isTyping: false,
        isWaiting: false,
        isStreaming: false,
        inferenceStatus: undefined,
        inferenceStage: undefined,
        inferenceStatusTimestamp: undefined,
        ...(hasContent
          ? { isTruncated: true }
          : {
              isError: true,
              hasError: true,
              content: hasThinking
                ? 'The model spent time reasoning but never produced an answer. Please try again.'
                : errorMessage,
            }),
      });
    });

    if (toast && streamingMessages.length > 0) {
      try {
        const { addToast } = useToastStore.getState().actions;
        addToast(errorMessage, 'error');
      } catch (_) { /* best effort */ }
    }

    return streamingMessages.length;
  }

  _startStreamWatchdog(conversationId) {
    if (!conversationId) return;
    // Only start watchdog if streaming has actually started
    if (!this.streamingStarted.get(conversationId)) {
      return;
    }
    
    this._clearStreamWatchdog(conversationId);

    const stallTimeoutMs = this._getStreamStallTimeoutMs(conversationId);

    const timer = setTimeout(async () => {
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      const lastActivity = this.streamActivityTimestamps.get(conversationId);
      const now = Date.now();
      // Re-evaluate: answer tokens may have started since the timer was armed.
      const activeStallTimeoutMs = this._getStreamStallTimeoutMs(conversationId);

      if (!chat || !chat.isInferencing) {
        this._clearStreamWatchdog(conversationId);
        return;
      }

      if (lastActivity && (now - lastActivity) < activeStallTimeoutMs) {
        this._startStreamWatchdog(conversationId);
        return;
      }

      this._recoverStalledConversation(conversationId, 'watchdog');
    }, stallTimeoutMs);

    this.streamWatchdogTimers.set(conversationId, timer);
  }

  async _recoverStalledConversation(conversationId, reason) {
    const chatStore = useChatStore.getState();
    const lastActivity = this.streamActivityTimestamps.get(conversationId);
    const timeSinceLastActivity = lastActivity ? Date.now() - lastActivity : null;

    devLog('[Socket] Checking stalled stream', {
      conversationId,
      reason,
      timeSinceLastActivity,
      isSocketConnected: this.isConnected,
    });

    // If socket is disconnected, don't recover - wait for reconnection
    // The socket will reconnect and we'll receive pending events
    if (!this.isConnected) {
      devLog('[Socket] Socket disconnected, skipping recovery - will retry on reconnect');
      // Restart the appropriate watchdog to check again later
      if (this.streamingStarted.get(conversationId)) {
        this._startStreamWatchdog(conversationId);
      } else {
        this._startPreprocessingWatchdog(conversationId);
      }
      return;
    }

    // Use the lightweight status endpoint first to avoid fetching full conversation
    let status = null;
    try {
      status = await chatsApi.getConversationStatus(conversationId);
    } catch (error) {
      devWarn('[Socket] Stream recovery: status endpoint failed, falling back to full fetch', error?.message || error);
    }

    // If status says backend is still generating, restart watchdog and wait
    // But track attempts to prevent infinite loops when backend is truly stuck
    if (status?.isInferencing) {
      const attempts = (this.stallRecoveryAttempts.get(conversationId) || 0) + 1;
      this.stallRecoveryAttempts.set(conversationId, attempts);
      const hasTimedActivity = typeof timeSinceLastActivity === 'number';
      const hasBeenIdleTooLong = hasTimedActivity && timeSinceLastActivity >= MAX_INFERENCE_IDLE_BEFORE_FORCE_FINISH_MS;

      if (attempts >= MAX_STALL_RECOVERY_ATTEMPTS && hasBeenIdleTooLong) {
        devLog('[Socket] Backend reports still inferencing but max recovery attempts reached — force-finishing', {
          conversationId,
          attempts,
          maxAttempts: MAX_STALL_RECOVERY_ATTEMPTS,
          timeSinceLastActivity,
          maxIdleBeforeForceFinishMs: MAX_INFERENCE_IDLE_BEFORE_FORCE_FINISH_MS,
        });
        // Force-finish: never leave an empty thinking bubble with no error.
        // Partial answers get Continue; thinking-only / empty turns get a
        // visible assistant error + toast.
        this._finalizeStalledAssistantMessages(conversationId, {
          errorMessage: 'The response stalled and never finished. Please try again.',
        });

        // Finalize skips media placeholders; still drop their streaming flags
        // so force-finish doesn't leave a forever-spinning card.
        const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
        (chat?.messages || []).forEach(m => {
          if (
            m.role === 'assistant' &&
            isAwaitingRequestedMedia(m) &&
            (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
          ) {
            chatStore.updateLocalChatMessage(conversationId, m.id, {
              isThinking: false,
              isTyping: false,
              isWaiting: false,
              isStreaming: false,
              inferenceStatus: undefined,
              inferenceStage: undefined,
              inferenceStatusTimestamp: undefined,
            });
          }
        });

        chatStore.updateLocalChatDetails(conversationId, {
          isInferencing: false,
          isInferencingWaiting: false,
        });

        this._clearStreamingState(conversationId);
        return;
      } else {
        devLog('[Socket] Backend still inferencing per status endpoint, continuing to wait', {
          conversationId,
          messageCount: status.messageCount,
          recoveryAttempt: attempts,
          maxAttempts: MAX_STALL_RECOVERY_ATTEMPTS,
          timeSinceLastActivity,
          maxIdleBeforeForceFinishMs: MAX_INFERENCE_IDLE_BEFORE_FORCE_FINISH_MS,
          hasBeenIdleTooLong,
        });
        if (this.streamingStarted.get(conversationId)) {
          this._startStreamWatchdog(conversationId);
        } else {
          this._startPreprocessingWatchdog(conversationId);
        }
        return;
      }
    }

    // In-flight turn guard: when the conversation is still waiting on its
    // first assistant reply and the send is very recent, a "not inferencing"
    // status response is not trustworthy — the status flag can lag job
    // registration by a few seconds. Clearing the waiting state here blanks
    // the thinking UI until stream_start arrives (the intermittent "thinking
    // drops, looks stopped, then restarts" flicker). Status payloads carrying
    // a completed assistant reply never reach this guard — they're injected
    // by the branch below — so background-completed turns still recover.
    if (status && !status.isInferencing) {
      const inFlightChat = useChatStore.getState().chats.find(c => c.id === conversationId);
      const inFlightMessages = inFlightChat?.messages || [];
      const inFlightLastMessage = inFlightMessages[inFlightMessages.length - 1];
      const inFlightLastTs = Date.parse(inFlightLastMessage?.timestamp || '');
      const hasActiveAssistantStream = inFlightMessages.some(m =>
        m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
      );
      const hasRecentTurnAwaitingReply = !hasActiveAssistantStream
        && inFlightLastMessage?.role === 'user'
        && !Number.isNaN(inFlightLastTs)
        && (Date.now() - inFlightLastTs) < RECOVERY_STATUS_TRUST_WINDOW_MS;
      if (hasRecentTurnAwaitingReply) {
        devLog('[Socket] Stream recovery: status reports done but a user turn is still in flight — waiting', {
          conversationId,
          reason,
        });
        if (this.streamingStarted.get(conversationId)) {
          this._startStreamWatchdog(conversationId);
        } else {
          this._startPreprocessingWatchdog(conversationId);
        }
        return;
      }
    }

    const currentChatForStatus = useChatStore.getState().chats.find(c => c.id === conversationId);
    const activeAssistantMessageCount = currentChatForStatus?.messages?.filter(m =>
      m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
    )?.length || 0;

    // If status has a completed last assistant message, inject it directly.
    // For multi-model generations, the status endpoint only carries the last
    // assistant message, so fall through to the full conversation refresh when
    // multiple assistant cards are still in flight.
    if (status && !status.isInferencing && status.lastMessage?.role === 'assistant' && activeAssistantMessageCount <= 1) {
      // Guard against a premature "completed" signal for media-generating
      // messages: the backend may briefly report `isInferencing: false`
      // (e.g. between extraction steps, or after a transient orchestrator
      // state change) while an async image/video/song/3D job is still in
      // flight. If the local message is still waiting on its requested
      // media artifact AND the status payload also doesn't carry that
      // artifact, we trust the local state and skip the recovery-driven
      // clear. Otherwise the loading placeholder would vanish while
      // gpt-image-2 / Fal video / song generation is still working
      // (these can take 30s-5min on the provider side).
      const lastMessageId = status.lastMessage?._id || status.lastMessage?.id;
      const lastMessageHasError = !!(status.lastMessage?.isError || status.lastMessage?.hasError);
      const currentChat = useChatStore.getState().chats.find(c => c.id === conversationId);
      const existingLocalMessage = currentChat?.messages?.find(m =>
        m.id === lastMessageId || m._id === lastMessageId
      );
      const requestedMediaKind = String(existingLocalMessage?.isRequestingMedia || '').toLowerCase();
      const localWaitingForMedia = isAwaitingRequestedMedia(existingLocalMessage);
      const lastMessageHasRequestedMedia = requestedMediaKind
        ? messageHasMediaOutput(status.lastMessage, requestedMediaKind)
        : false;

      if (localWaitingForMedia && !lastMessageHasRequestedMedia && !lastMessageHasError) {
        devLog('[Socket] Stream recovery: skipping injection — local message still waiting on async media', {
          conversationId,
          lastMessageId,
          requestedMediaKind,
        });
        // Restart the appropriate watchdog and keep waiting for the real
        // `assistant_message_image_stream` event to land.
        if (this.streamingStarted.get(conversationId)) {
          this._startStreamWatchdog(conversationId);
        } else {
          this._startPreprocessingWatchdog(conversationId);
        }
        return;
      }

      devLog('[Socket] Stream recovery: injecting completed message from status endpoint', {
        conversationId,
        lastMessageId,
        hadLocalMessage: !!existingLocalMessage,
      });

      const mappedMessage = mapSerializedMessageToMessage({
        ...status.lastMessage,
        isThinking: false,
        isTyping: false,
        isWaiting: false,
        isStreaming: false,
      });
      mappedMessage.inferenceStatus = undefined;
      mappedMessage.inferenceStage = undefined;
      mappedMessage.inferenceStatusTimestamp = undefined;

      // Critical for native background/foreground: if the app was suspended
      // during Thinking, `new_assistant_message` / token events are often
      // missed, so the completed assistant message is not in local state yet.
      // `updateLocalChatMessage` no-ops when the id is missing — previously we
      // still cleared isInferencing/isInferencingWaiting and returned, which
      // left a blank gap under the user message (no Thinking UI, no answer).
      let injected = false;
      if (lastMessageId) {
        const updated = chatStore.updateLocalChatMessage(conversationId, lastMessageId, mappedMessage);
        if (updated) {
          injected = true;
        } else {
          chatStore.addMessage(conversationId, mappedMessage);
          const chatAfterAdd = useChatStore.getState().chats.find(c => c.id === conversationId);
          injected = !!(chatAfterAdd?.messages?.find(m =>
            m.id === lastMessageId || m._id === lastMessageId
          ));
          if (injected) {
            devLog('[Socket] Stream recovery: added missing completed message to local state', {
              conversationId,
              lastMessageId,
            });
          }
        }
      }

      if (!injected) {
        // Don't tear down Thinking/waiting — fall through to full conversation
        // refresh so we don't blank the UI on a failed inject.
        devWarn('[Socket] Stream recovery: status inject failed, falling through to full fetch', {
          conversationId,
          lastMessageId,
        });
      } else {
        // Clear any remaining streaming placeholders (e.g. orphan empty
        // assistant cards from a partial stream before suspend).
        const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
        const streamingMessages = chat?.messages?.filter(m =>
          m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
        ) || [];

        streamingMessages.forEach(streamingMessage => {
          const isInjectedMessage = streamingMessage.id === lastMessageId || streamingMessage._id === lastMessageId;
          if (isInjectedMessage) return;
          chatStore.updateLocalChatMessage(conversationId, streamingMessage.id, {
            isThinking: false,
            isTyping: false,
            isWaiting: false,
            isStreaming: false,
            inferenceStatus: undefined,
            inferenceStage: undefined,
            inferenceStatusTimestamp: undefined
          });
        });

        chatStore.updateLocalChatDetails(conversationId, {
          isInferencing: false,
          isInferencingWaiting: false,
        });

        this._clearStreamingState(conversationId);
        return;
      }
    }

    // Fallback: fetch full conversation if status endpoint didn't have what we need
    const statusConfirmedDone = status && !status.isInferencing;
    let conversation = null;
    try {
      conversation = await chatsApi.getConversationById(conversationId);
      if (conversation) {
        const currentChatState = useChatStore.getState().chats.find(c => c.id === conversationId);

        // If the status endpoint already confirmed the backend is done, trust it
        // and clear the client's inferencing state so addChats can apply the update.
        // Without this, the client loops: status says "done" → client says "still
        // inferencing" → skip update → watchdog restarts → repeat.
        if (currentChatState?.isInferencing && statusConfirmedDone) {
          devLog('[Socket] Stream recovery: Backend confirmed done — clearing client inferencing state before addChats', {
            conversationId,
            currentMessageCount: currentChatState?.messages?.length,
            backendMessageCount: conversation?.messages?.length,
          });
          chatStore.updateLocalChatDetails(conversationId, {
            isInferencing: false,
            isInferencingWaiting: false,
          });
        } else if (currentChatState?.isInferencing) {
          // Status was unavailable — we can't confirm the backend is done,
          // so preserve streaming state to avoid overwriting active data.
          devLog('[Socket] Stream recovery: Chat is still inferencing and status unconfirmed, skipping addChats to preserve streaming state', {
            conversationId,
            currentMessageCount: currentChatState?.messages?.length,
            backendMessageCount: conversation?.messages?.length,
          });
          if (this.streamingStarted.get(conversationId)) {
            this._startStreamWatchdog(conversationId);
          } else {
            this._startPreprocessingWatchdog(conversationId);
          }
          return;
        }
        chatStore.addChats([conversation]);
      }
    } catch (error) {
      devWarn('[Socket] Stream recovery failed to refresh conversation', error?.message || error);
      if (this.streamingStarted.get(conversationId)) {
        this._startStreamWatchdog(conversationId);
      } else {
        this._startPreprocessingWatchdog(conversationId);
      }
      return;
    }

    // Only clear state if we're confident the backend is done
    devLog('[Socket] Recovering stalled stream - backend appears done', {
      conversationId,
      reason,
      timeSinceLastActivity,
    });

    const refreshedChat = useChatStore.getState().chats.find(c => c.id === conversationId);
    const streamingMessages = refreshedChat?.messages?.filter(m =>
      m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
    ) || [];

    streamingMessages.forEach(streamingMessage => {
      chatStore.updateLocalChatMessage(conversationId, streamingMessage.id, {
        isThinking: false,
        isTyping: false,
        isWaiting: false,
        isStreaming: false,
        inferenceStatus: undefined,
        inferenceStage: undefined,
        inferenceStatusTimestamp: undefined
      });
    });

    chatStore.updateLocalChatDetails(conversationId, {
      isInferencing: false,
      isInferencingWaiting: false,
    });

    this._clearStreamingState(conversationId);
  }

  // Initialize socket connection
  connect(token, userId) {
    // If no token provided, don't connect
    if (!token) {
      devWarn('No token provided for socket connection');
      return;
    }
    // If no user ID provided, don't connect
    if (!userId) {
      devWarn('No user ID provided for socket connection');
      return;
    }
    // If socket exists, check if we need to reconnect with new token. We
    // compare against this.lastToken (not socket.auth) because auth is now a
    // function (below) that refreshes per-attempt, so socket.auth no longer
    // holds a static token to compare against.
    if (this.socket) {
      // If token hasn't changed, no need to reconnect
      if (this.lastToken === token) {
        return;
      }

      // Token has changed, cleanup existing connection
      this.disconnectSocket();
      this.socket.disconnect();
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
    }

    this.lastToken = token; // Store the token
    this.userId = userId; // Store the user ID
    this.socket = io(SOCKET_HOST, {
      // Auth as a FUNCTION so socket.io re-evaluates it before EVERY connection
      // attempt — including automatic reconnects after a blue/green deploy.
      // With a static auth object, a deploy drops every client and socket.io
      // reconnects using the token captured at the original connect(), which
      // may have crossed its 1h expiry during the cutover -> a burst of
      // "invalid access token" handshakes on the new server (appSocket.js:144).
      // getValidToken refreshes if needed; reconnectSocket:false because the
      // socket is already (re)connecting here, so a second socketManager.connect
      // would race. Refresh outages wait with backoff instead of repeatedly
      // sending the expired lastToken to the server.
      auth: (cb) => {
        const connectingSocket = this.socket;
        useAuthStore.getState().getValidToken({ reconnectSocket: false })
          .then((freshToken) => {
            if (this.socket !== connectingSocket) return;
            if (!freshToken) { this.disconnectSocket(); return; }
            this.lastToken = freshToken;
            cb({ token: freshToken, platform: Platform.OS });
          })
          .catch(() => {
            if (this.socket === connectingSocket) this._scheduleAuthReconnect(false);
          });
      },
      // Start with HTTP polling, then upgrade to WebSocket for token streaming.
      // Polling remains available when a network blocks WebSocket upgrades.
      // This requires Caddy's srv_id affinity throughout polling and upgrade.
      transports: ['polling','websocket'],
      tryAllTransports: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      // Temporary outages must not leave the app permanently disconnected.
      // The capped backoff above limits retry frequency during long outages.
      reconnectionAttempts: Infinity,
      withCredentials: true,
      autoConnect: true,
      path: '/socket.io',
      forceNew: true,
      timeout: 10000,
      debug: true
    });

    // Set up default event handlers
    this._setupUserEventHandlers();

    // Setup engine-level events
    this._setupEngineEventHandlers();

    // Ensure the lifecycle listener is active. disconnectSocket() (called on
    // logout or token change) tears it down, so it must be restored after
    // re-authentication on both native and web.
    if (Platform.OS !== 'web') {
      if (!this.appStateSubscription) this._setupAppStateListener();
    } else if (!this.visibilityHandler) {
      this._setupVisibilityListener();
    }
  }

  // Schedule a reconnect that waits for `conversation_stream_end` so we don't
  // kill an active stream when the access token changes mid-flight. Always
  // also schedules a fallback so a missed stream_end event (socket drop,
  // service worker interception, orchestrator crash) can never strand the
  // socket on an expired token forever.
  scheduleDeferredReconnect(token, userId) {
    if (!token || !userId) {
      devWarn('[Socket] scheduleDeferredReconnect: missing token or userId');
      return;
    }
    this._pendingReconnect = true;
    this._deferredReconnectToken = token;
    this._deferredReconnectUserId = userId;

    // Clear any prior fallback timer before scheduling a new one.
    if (this._deferredReconnectTimer) {
      clearTimeout(this._deferredReconnectTimer);
    }
    // 5 min fallback — well beyond any legitimate stream end delay (the
    // server-side inference lock sweep at 10 min is the next safety net).
    this._deferredReconnectTimer = setTimeout(() => {
      if (!this._pendingReconnect) return;
      devWarn('[Socket] Deferred reconnect fallback fired — stream_end was missed, forcing reconnect');
      this._clearDeferredReconnect();
      try {
        this.connect(token, userId);
      } catch (e) {
        devWarn('[Socket] Deferred reconnect fallback failed:', e?.message || e);
      }
    }, 5 * 60 * 1000);
  }

  _clearDeferredReconnect() {
    this._pendingReconnect = false;
    this._deferredReconnectToken = null;
    this._deferredReconnectUserId = null;
    if (this._deferredReconnectTimer) {
      clearTimeout(this._deferredReconnectTimer);
      this._deferredReconnectTimer = null;
    }
  }

  _scheduleAuthReconnect(forceRefresh) {
    if (!this._authRecovery) this._authRecovery = createSocketAuthRecovery({
      getSocket: () => this.socket,
      getAuthState: () => useAuthStore.getState(),
    });
    this._authRecovery.schedule(forceRefresh);
  }

  // Disconnect socket
  disconnectSocket() {
    this._authRecovery?.cancel();
    if (this.socket) {
      // Unsubscribe from user channel
      if (this.userId) {
        this.socket.emit("unsubscribe", { userId: this.userId });
      }

      // Clean up user-level event listeners
      this.socket.off('connect');
      this.socket.off('connect_error');
      this.socket.off('disconnect');
      this.socket.off('error');
      this.socket.off('upgrade');
      this.socket.off('upgradeError');
      this.socket.off('pong');
      
      // Clean up business logic events
      this.socket.off('message_reactions');
      this.socket.off('generalError');
      this.socket.off('send_prompt_error');
      this.socket.off('regenerate_message_error');
      this.socket.off('trigger_conversation_socket_inference_error');
      this.socket.off('reload');
      this.socket.off('memory_created');
      this.socket.off('memory_updated');
      this.socket.off('memory_deleted');
      this.socket.off('memory_archived');
      this.socket.off('memory_profile_updated');
      this.socket.off('memory_save_failed');
      this.socket.off('follow_up_suggestions');
      this.socket.off('collection_suggestion');

      // Clean up voice mode media events
      this.socket.off('voice_media_started');
      this.socket.off('voice_media_progress');
      this.socket.off('voice_media_complete');
      this.socket.off('voice_media_error');
      this.socket.off('voice_transcript_saved');
      this.socket.off('voice_transcript_complete');
      this.socket.off('voice_processing_started');
      this.socket.off('voice_processing_complete');

      // Clean up chat-related events
      this.socket.off('conversation_created');
      this.socket.off('conversation_deleted');
      this.socket.off('new_user_message');
      this.socket.off('user_message_error');
      this.socket.off('conversation_title');
      this.socket.off('conversation_upgraded_to_work');
      this.socket.off('work_mode_choice');
      this.socket.off('conversation_stream_start');
      this.socket.off('new_assistant_message');
      this.socket.off('assistant_message_token_stream');
      this.socket.off('assistant_message_thinking_stream');
      this.socket.off('inference_status');
      this.socket.off('assistant_message_image_stream');
      this.socket.off('conversation_stream_end');

      // Clean up file processing events
      this.socket.off('file_processing_complete');

      // Clean up fast mode events
      this.socket.off('fastModeToggled');
      this.socket.off('fastModeError');
      this.socket.off('referral_credit');

      // Clean up engine-level events if they exist
      if (this.socket.io) {
        this.socket.io.off('reconnect_attempt');
        this.socket.io.off('reconnect_error');
        this.socket.io.off('reconnect_failed');
        this.socket.io.off('transport');
      }

      // Disconnect and close socket
      this.socket.disconnect();
      this.socket.close();
    }
    
    // Clear all socket state
    this.socket = null;
    this.isConnected = false;
    this.lastToken = null;
    this.userId = null;

    // Tear down any deferred-reconnect state so a missed stream_end can never
    // fire a stray reconnect after logout / token change.
    this._clearDeferredReconnect();
    
    if (this.tokenStreamInterval) {
      clearInterval(this.tokenStreamInterval);
      this.tokenStreamInterval = null;
    }
    this.tokenStreamBuffer.clear();
    
    // Clean up AppState / visibility listeners
    if (this.appStateSubscription) {
      this.appStateSubscription.remove();
      this.appStateSubscription = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.onlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this._crossTabChatSyncUnsub) {
      this._crossTabChatSyncUnsub();
      this._crossTabChatSyncUnsub = null;
    }
    // Drop any paused-due-to-hidden flag so a later reconnect isn't skipped.
    this._pausedDueToHidden = false;
  }

  isSocketConnected() {
    return this.isConnected && this.socket;
  }

  // True if the most recent handshake was rejected with HTTP 429 and the
  // Retry-After window hasn't elapsed yet. requireConnection() uses this to
  // pick the right user-facing message.
  isRateLimited() {
    return !!this._rateLimitedUntilMs && Date.now() < this._rateLimitedUntilMs;
  }

  // Connection gate for send paths. Returns a Promise<boolean>: resolves true
  // when connected (waiting out a short reconnect window first — see below);
  // resolves false after surfacing the appropriate toast:
  //   - When rate-limited by the server (HTTP 429): an info toast saying
  //     we're reconnecting. "Check your internet connection" is actively
  //     wrong here — the user's network is fine, the server asked us to
  //     slow down.
  //   - Otherwise: the standard "check your internet connection" toast.
  //
  // If the socket object exists but is momentarily disconnected (pause-on-
  // hidden resume, post-background resync, deploy reconnect, mid-backoff),
  // we kick an immediate connect attempt and wait up to
  // REQUIRE_CONNECTION_WAIT_MS for it to land instead of erroring at once —
  // these windows are self-inflicted and almost always resolve within a
  // second or two, so the user should never see a connection error for them.
  // An info "Reconnecting…" toast appears only if the wait exceeds
  // REQUIRE_CONNECTION_INFO_TOAST_AFTER_MS, and is dismissed on success.
  //
  // Callers must `await` this and check the resolved boolean (the send
  // handlers in g/[id].jsx etc).
  async requireConnection() {
    if (this.isSocketConnected()) return true;

    const startedAt = Date.now();
    this._requireConnectionInfoToastShown = false;

    if (this.socket) {
      // A reconnect is plausible — the manager exists and will retry. Kick it
      // now rather than waiting out the current backoff timer, and clear the
      // pause-on-hidden flag so a later visibilitychange doesn't double-resume.
      this._pausedDueToHidden = false;
      try {
        this.socket.connect();
      } catch (e) {
        devWarn('[Socket] requireConnection: connect kick failed:', e?.message || e);
      }

      const deadline = startedAt + REQUIRE_CONNECTION_WAIT_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, REQUIRE_CONNECTION_POLL_MS));
        if (this.isSocketConnected()) return true;

        // Still down after the info-toast threshold — tell the user we're
        // working on it (once; addToast dedupe isn't guaranteed, so track it).
        if (
          Date.now() - startedAt >= REQUIRE_CONNECTION_INFO_TOAST_AFTER_MS &&
          !this._requireConnectionInfoToastShown
        ) {
          this._requireConnectionInfoToastShown = true;
          try {
            useToastStore.getState().actions.addToast(
              'Reconnecting to server — please wait a moment.',
              'info',
              [{ label: 'Run Diagnostics', href: '/diagnostics' }],
              { category: 'connection' },
            );
          } catch (_) {}
        }
      }
    }

    let addToast;
    let removeToastsByCategory;
    try {
      const toastActions = useToastStore.getState().actions;
      addToast = toastActions.addToast;
      removeToastsByCategory = toastActions.removeToastsByCategory;
    } catch (_) {
      addToast = null;
    }

    // If the socket came back between the deadline expiring and the toast
    // firing (polling race), succeed instead of showing a stale error.
    if (this.isSocketConnected()) return true;

    if (this.isRateLimited()) {
      addToast?.(
        'Reconnecting to server — please wait a moment.',
        'info',
        [{ label: 'Run Diagnostics', href: '/diagnostics' }],
        { category: 'connection' },
      );
    } else {
      // navigator.onLine is only a hint, but when it explicitly says we're
      // offline the "check your internet" wording is justified. When it says
      // we're online and we still can't connect, blame the server, not the
      // user's network — diagnostics-verified healthy connections were being
      // contradicted by this toast.
      const offline =
        typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
          ? !navigator.onLine
          : false;
      addToast?.(
        offline
          ? 'Please check your internet connection and try again.'
          : 'Having trouble connecting to the server. Please try again in a moment.',
        'error',
        [{ label: 'Run Diagnostics', href: '/diagnostics' }],
        { category: 'connection' },
      );
    }
    return false;
  }

  // Set up default event handlers
  _setupUserEventHandlers() {
    if (!this.socket) return;

    // Handle authentication errors separately from other errors
    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);

      // Detect HTTP 429 (Too Many Requests). Our own handshake limiter now
      // delays instead of returning 429, but this still fires for Cloudflare,
      // upstream proxies, or any future server-side change. socket.io's error
      // shape varies by transport and client version, so check the usual
      // places where the underlying HTTP response may surface.
      const httpStatus =
        error?.context?.statusCode ||
        error?.context?.status ||
        error?.description?.statusCode ||
        error?.description?.status ||
        error?.req?.res?.statusCode ||
        error?.status;

      if (httpStatus === 429) {
        // Parse Retry-After (seconds), defaulting to 5 and clamping at 60
        // so a malformed/malicious header can't park us for minutes.
        let retryAfterSec = 5;
        const headerSource =
          error?.context?.headers ||
          error?.description?.headers ||
          error?.req?.res?.headers ||
          {};
        const retryAfterRaw = headerSource['retry-after'] || headerSource['Retry-After'];
        const parsed = parseInt(retryAfterRaw, 10);
        if (Number.isFinite(parsed) && parsed > 0) {
          retryAfterSec = Math.min(parsed, 60);
        }
        this._rateLimitedUntilMs = Date.now() + retryAfterSec * 1000;

        // Raise socket.io's backoff floor so the next reconnect attempt
        // waits at least as long as the server asked. Without this, each
        // retry is a fresh handshake with no `sid` and re-poisons whatever
        // rate limit bucket just rejected us — the exact reconnect-storm
        // loop we're trying to break. Original value is restored on
        // successful connect.
        if (this.socket?.io?.opts) {
          if (!this._origReconnectionDelay) {
            this._origReconnectionDelay = this.socket.io.opts.reconnectionDelay;
          }
          this.socket.io.opts.reconnectionDelay = Math.max(
            this.socket.io.opts.reconnectionDelay || 0,
            retryAfterSec * 1000,
          );
        }
        devWarn(`[Socket] Handshake rate-limited (429). Backing off for ${retryAfterSec}s.`);
      } else {
        // Non-429 error — clear the flag so requireConnection() shows the
        // normal "check internet" toast instead of "reconnecting".
        this._rateLimitedUntilMs = 0;
      }

      // Namespace auth rejections need an explicit retry: Socket.IO does not
      // reconnect them automatically. Refresh rejected credentials, but keep
      // current credentials on outages; both paths use bounded backoff.
      if (error.data?.code || error.message === 'Authentication error' || error.message.includes('jwt')) {
        this._scheduleAuthReconnect(error.data?.retryable !== true);
      }

      console.error('Error details:', {
        type: error.type,
        description: error.description,
        context: error.context,
        transport: this.socket?.io?.engine?.transport?.name
      });
      this.isConnected = false;
    });

    // Handle general errors
    // The backend sends generalError as a fallback alongside specific error events.
    // On native, we use this as a safety net to clear loading state if the specific
    // handler didn't fire or failed.
    this.socket.on('generalError', (data) => {
      console.log('[Socket] generalError received:', JSON.stringify(data));
      const { msg, modal, title, reason, conversationId, actions, errorCode } = data || {};

      const {
        isModelAccessError,
        isCreditError,
        isPlusError,
      } = classifyResponseError({ actions, reason, errorCode, errorMessage: msg });
      const isCreditMessage = !isModelAccessError && !isPlusError
        && msg && (msg.toLowerCase().includes('credit') || msg.toLowerCase().includes('insufficient'));
      // Email-verification gate — handled by a dedicated modal, not a toast.
      // The specific error events (send_prompt_error etc.) open the same modal;
      // opening it again here is an idempotent state set, and this catches
      // backend paths that only emit generalError.
      const isVerifyPhoneError = this._isVerifyPhoneError(actions, msg);
      const isVerifyEmailError = this._isVerifyEmailError(actions, msg);
      const isReviewHoldError = this._refreshReviewHoldState(msg, errorCode, 'general_error');
      
      // Only treat as content moderation when the server explicitly says so.
      // The old blanket fallback classified ANY native non-credit error as
      // moderation, which made transient failures look like policy rejections
      // and swallowed them silently (no toast).
      const isModerationError = this._isContentModerationError(actions, reason, errorCode);

      if (isVerifyPhoneError || isVerifyEmailError || isReviewHoldError) {
        if (isVerifyPhoneError) this._openVerifyPhoneModal('general_error');
        else if (isVerifyEmailError) this._openVerifyEmailModal('general_error');
        // Safety fallback: clear loading state if we have a conversationId.
        if (conversationId) {
          try {
            this._markConversationRejected(conversationId);
            useChatStore.getState().updateLocalChatDetails(conversationId, {
              isInferencing: false,
              isInferencingWaiting: false,
              pendingInferenceStatus: undefined,
              pendingInferenceStage: undefined,
              pendingCanFastAnswer: undefined,
              pendingInferenceStatusTimestamp: undefined,
            });
            this._clearStreamingState(conversationId);
          } catch (e) {
            console.error('[Socket] generalError: Failed to clear loading state:', e);
          }
        }
      } else if (isModerationError) {
        // No toast — inline card handles display.
        // As a safety fallback, clear loading state if we have a conversationId.
        if (conversationId) {
          try {
            this._markConversationRejected(conversationId);
            useChatStore.getState().updateLocalChatDetails(conversationId, {
              isInferencing: false,
              isInferencingWaiting: false,
              pendingInferenceStatus: undefined,
              pendingInferenceStage: undefined,
              pendingCanFastAnswer: undefined,
              pendingInferenceStatusTimestamp: undefined,
            });
            this._clearStreamingState(conversationId);
          } catch (e) {
            console.error('[Socket] generalError: Failed to clear loading state:', e);
          }
        }
      } else if (isModelAccessError) {
        try {
          const { openModal } = useModalStore.getState().actions;
          openModelAccessPaywall(openModal, {
            title: 'Unlock Premium AI Models',
            subtitle: msg || 'To use this model, buy credits or upgrade to Plus.',
          });
        } catch (e) {
          console.error('[Socket] generalError: Failed to open model access paywall:', e);
        }
      } else if (msg && !modal && !isCreditError && !isCreditMessage && !isPlusError) {
        const { addToast } = useToastStore.getState().actions;
        addToast(msg, 'error');
      }

      // If credit error, refresh credits
      if (isCreditError || isCreditMessage) {
        try {
          const authStore = useAuthStore.getState();
          if (authStore?.fetchUserCredits) authStore.fetchUserCredits();
        } catch (e) { /* best effort */ }
      }
    });

    this.socket.on('send_prompt_error', (data) => {
      console.log('[Socket] send_prompt_error received:', JSON.stringify(data));
      const { conversationId, messageId, errorMessage, actions, reason, errorCode } = data || {};

      // ALWAYS clear loading state first — before any type detection or message handling.
      // This is the most critical action: stop the loading spinner no matter what.
      try {
        if (conversationId) {
          // Mark rejected early so late-arriving events (stream_start, inference_status)
          // don't re-enable loading
          this._markConversationRejected(conversationId);
          useChatStore.getState().updateLocalChatDetails(conversationId, {
            isInferencing: false,
            isInferencingWaiting: false,
            pendingInferenceStatus: undefined,
            pendingInferenceStage: undefined,
            pendingCanFastAnswer: undefined,
            pendingInferenceStatusTimestamp: undefined,
          });
          this._clearStreamingState(conversationId);
        }
      } catch (e) {
        console.error('[Socket] send_prompt_error: Failed to clear loading state:', e);
      }

      this._restorePendingContinuation(conversationId);

      try {
        const {
          isModelAccessError,
          isCreditError,
          isPlusError,
        } = classifyResponseError({ actions, reason, errorCode, errorMessage });
        // Email-verification gate — handled by a dedicated modal, not a toast.
        const isVerifyPhoneError = this._isVerifyPhoneError(actions, errorMessage);
        const isVerifyEmailError = this._isVerifyEmailError(actions, errorMessage);
        const isReviewHoldError = this._refreshReviewHoldState(errorMessage, errorCode, 'send_prompt_error');
        
        // Only treat as content moderation when the server explicitly indicates
        // it. Previously any non-credit/non-plus native error was classified as
        // moderation, which deleted the user's pending message and showed an
        // "I can't help with that" card for transient server/network failures.
        const isModerationError = this._isContentModerationError(actions, reason, errorCode);

        // Toast logic
        if (isVerifyPhoneError) {
          this._openVerifyPhoneModal('send_prompt_error');
        } else if (isVerifyEmailError) {
          // No toast — VerifyEmailModal handles resend + support contact
          this._openVerifyEmailModal('send_prompt_error');
        } else if (isReviewHoldError) {
          // No toast — AccountReviewHoldModal handles support contact
        } else if (isModelAccessError || isCreditError || isPlusError) {
          // No toast — inline message / paywall handles display
        } else if (isModerationError) {
          // No toast — inline moderation card handles display
        } else if (errorMessage) {
          const cleanActions = Array.isArray(actions) && actions.length > 0 ? actions : undefined;
          const { addToast } = useToastStore.getState().actions;
          addToast(errorMessage, 'error', cleanActions);
        } else {
          // Never fail silently — generic errors previously fell into the
          // moderation branch on native, which suppressed the toast entirely.
          const { addToast } = useToastStore.getState().actions;
          addToast('Something went wrong. Please try again.', 'error');
        }

        // If credit error, refresh credits
        if (isCreditError) {
          try {
            const authStore = useAuthStore.getState();
            if (authStore?.fetchUserCredits) authStore.fetchUserCredits();
          } catch (e) { /* best effort */ }
        }

        // Handle message state based on error type
        if (isModelAccessError || isCreditError || isPlusError) {
          const errorType = isModelAccessError
            ? (reason || 'model_plus_only')
            : isCreditError
              ? (reason || 'INSUFFICIENT_CREDITS')
              : (reason || 'PLUS_REQUIRED');
          const updates = {isError: true, buttons: actions || undefined, errorType};
          if (!messageId) {
            updates.id = `error-${Crypto.randomUUID()}`;
          }
          useChatStore.getState().updateLocalChatMessage(conversationId, messageId, updates);
        } else if (isModerationError) {
          // Content moderation on native: show inline moderation card
          this._markConversationRejected(conversationId);
          if (messageId) {
            // Assistant message already exists — convert it into a moderation message
            useChatStore.getState().updateLocalChatMessage(conversationId, messageId, {
              isError: true,
              content: errorMessage || "I can't help with that request.",
              moderationMessage: errorMessage || "I can't help with that request.",
              buttons: [{ action: RESPONSE_ACTION_TYPES.CONTENT_MODERATION }],
              isWaiting: false,
              isStreaming: false,
              isThinking: false,
              isTyping: false,
            });
          } else {
            // No assistant message yet — remove pending user message and add moderation card
            try {
              const chatState = useChatStore.getState();
              const chat = chatState.chats.find(c => c.id === conversationId);
              const pendingMsg = [...(chat?.messages || [])].reverse().find(m =>
                m.role === 'user' && 
                (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
              );
              if (pendingMsg?.id) {
                chatState.removeMessage(conversationId, pendingMsg.id);
              }
            } catch (e) {
              console.error('[Socket] send_prompt_error: Failed to remove pending message:', e);
            }
            try {
              this._addModerationMessage(conversationId, errorMessage);
            } catch (e) {
              console.error('[Socket] send_prompt_error: Failed to add moderation message:', e);
            }
          }
        } else if (!messageId) {
          // Non-credit/plus/moderation error with no messageId — keep the user's text recoverable.
          const chatState = useChatStore.getState();
          const chat = chatState.chats.find(c => c.id === conversationId);
          const pendingMsg = [...(chat?.messages || [])].reverse().find(m =>
            m.role === 'user' && 
            (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
          );
          if (pendingMsg?.id) {
            chatState.updateLocalChatMessage(conversationId, pendingMsg.id, {
              isError: true,
              errorMessage,
              buttons: actions || undefined,
            });
          }
        } else {
          useChatStore.getState().updateLocalChatMessage(conversationId, messageId, {
            isError: true,
            buttons: actions || undefined,
          });
        }
      } catch (error) {
        console.error('[Socket] Error handling send_prompt_error:', error);
      }
    });

    this.socket.on('regenerate_message_error', (data) => {
      console.log('[Socket] regenerate_message_error received:', JSON.stringify(data));
      const { conversationId, messageId, errorMessage, actions, reason, errorCode } = data || {};

      // ALWAYS clear loading state first — stop the loading spinner no matter what.
      try {
        if (conversationId) {
          // Mark rejected early so late-arriving events don't re-enable loading
          this._markConversationRejected(conversationId);
          useChatStore.getState().updateLocalChatDetails(conversationId, {
            isInferencing: false,
            isInferencingWaiting: false,
            pendingInferenceStatus: undefined,
            pendingInferenceStage: undefined,
            pendingCanFastAnswer: undefined,
            pendingInferenceStatusTimestamp: undefined,
          });
          this._clearStreamingState(conversationId);
        }
      } catch (e) {
        console.error('[Socket] regenerate_message_error: Failed to clear loading state:', e);
      }

      // PLUS_WINBACK_PRESSURE §2: when regenerate is rejected because
      // the target message lives in the locked range, route the user
      // straight to the upgrade modal instead of showing a toast or
      // inline credits/plus error card — those error cards exist for
      // running-out-of-credits flows and would be misleading copy
      // here. We still clear the loading/streaming state above so the
      // chat isn't left in an inferencing limbo.
      if (errorCode === 'plus_locked') {
        try {
          const { openModal } = useModalStore.getState().actions;
          openModal('CreditsPurchaseModal', { initialTab: 'subscribe', source: 'plus-lock-regenerate' });
        } catch (e) {
          console.error('[Socket] regenerate_message_error: Failed to open upgrade modal:', e);
        }
        return;
      }

      try {
        const {
          isModelAccessError,
          isCreditError,
          isPlusError,
        } = classifyResponseError({ actions, reason, errorCode, errorMessage });
        // Only treat as content moderation when the server explicitly says so
        // (same rationale as send_prompt_error — no blanket native fallback).
        const isModerationError = this._isContentModerationError(actions, reason, errorCode);
        
        // Toast logic
        if (isModelAccessError || isCreditError || isPlusError || isModerationError) {
          // No toast — inline message components handle display
        } else if (errorMessage) {
          const cleanActions = Array.isArray(actions) && actions.length > 0 ? actions : undefined;
          const { addToast } = useToastStore.getState().actions;
          addToast(errorMessage, 'error', cleanActions);
        }

        // If credit error, refresh credits
        if (isCreditError) {
          try {
            const authStore = useAuthStore.getState();
            if (authStore?.fetchUserCredits) authStore.fetchUserCredits();
          } catch (e) { /* best effort */ }
        }

        // Unhide the old message so it remains visible and the regenerate button stays available
        if (messageId) {
          useChatStore.getState().updateLocalChatMessage(conversationId, messageId, {
            isHidden: false,
          });
        }

        // For credit/plus/model-access errors on regenerate: add an inline error message so the card renders
        if (isModelAccessError || isCreditError || isPlusError) {
          const errorType = isModelAccessError
            ? (reason || 'model_plus_only')
            : isCreditError
              ? (reason || 'INSUFFICIENT_CREDITS')
              : (reason || 'PLUS_REQUIRED');
          const errorUpdate = {
            id: `error-${Crypto.randomUUID()}`,
            isError: true,
            buttons: actions || undefined,
            errorType,
            content: errorMessage || '',
            role: 'assistant',
          };
          useChatStore.getState().updateLocalChatMessage(conversationId, null, errorUpdate);
        }

        // For native moderation: add inline moderation message
        if (isModerationError) {
          this._markConversationRejected(conversationId);
          try {
            this._addModerationMessage(conversationId, errorMessage);
          } catch (e) {
            console.error('[Socket] regenerate_message_error: Failed to add moderation message:', e);
          }
        }
      } catch (error) {
        console.error('[Socket] Error handling regenerate_message_error:', error);
      }
    });

    this.socket.on('trigger_conversation_socket_inference_error', (data) => {
      console.log('[Socket] trigger_conversation_socket_inference_error received:', JSON.stringify(data));
      const { conversationId, messageId, errorMessage, actions, reason, errorCode } = data || {};

      // ALWAYS clear loading state first — this is the most critical action.
      // Mark rejected before clearing so late events don't re-enable loading.
      try {
        if (conversationId) {
          this._markConversationRejected(conversationId);
          useChatStore.getState().updateLocalChatDetails(conversationId, {
            isInferencing: false,
            isInferencingWaiting: false,
            pendingInferenceStatus: undefined,
            pendingInferenceStage: undefined,
            pendingCanFastAnswer: undefined,
            pendingInferenceStatusTimestamp: undefined,
          });
          this._clearStreamingState(conversationId);
          const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
          const streamingMessages = chat?.messages?.filter(m =>
            m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
          ) || [];
          streamingMessages.forEach(streamingMessage => {
            useChatStore.getState().updateLocalChatMessage(conversationId, streamingMessage.id, {
              isThinking: false,
              isTyping: false,
              isWaiting: false,
              isStreaming: false,
              inferenceStatus: undefined,
              inferenceStage: undefined,
              inferenceStatusTimestamp: undefined,
            });
          });
        }
      } catch (e) {
        console.error('[Socket] trigger_conversation_socket_inference_error: Failed to clear loading state:', e);
      }

      try {
        const {
          isModelAccessError,
          isCreditError,
          isPlusError,
        } = classifyResponseError({ actions, reason, errorCode, errorMessage });
        // Email-verification gate — handled by a dedicated modal, not a toast.
        const isVerifyPhoneError = this._isVerifyPhoneError(actions, errorMessage);
        const isVerifyEmailError = this._isVerifyEmailError(actions, errorMessage);
        const isReviewHoldError = this._refreshReviewHoldState(errorMessage, errorCode, 'conversation_inference_error');
        // Only treat as content moderation when the server explicitly says so
        // (same rationale as send_prompt_error — no blanket native fallback).
        const isModerationError = this._isContentModerationError(actions, reason, errorCode);
        
        // Toast logic
        if (isVerifyPhoneError) {
          this._openVerifyPhoneModal('conversation_inference_error');
        } else if (isVerifyEmailError) {
          // No toast — VerifyEmailModal handles resend + support contact
          this._openVerifyEmailModal('conversation_inference_error');
        } else if (isReviewHoldError) {
          // No toast — AccountReviewHoldModal handles support contact
        } else if (isModelAccessError || isCreditError || isPlusError || isModerationError) {
          // No toast — inline message components handle display
        } else if (errorMessage) {
          const cleanActions = Array.isArray(actions) && actions.length > 0 ? actions : undefined;
          const { addToast } = useToastStore.getState().actions;
          addToast(errorMessage, 'error', cleanActions);
        }

        // If credit error, refresh credits
        if (isCreditError) {
          try {
            const authStore = useAuthStore.getState();
            if (authStore?.fetchUserCredits) authStore.fetchUserCredits();
          } catch (e) { /* best effort */ }
        }

        if (isModerationError) {
          // Content moderation on native: show inline moderation card.
          // User's message was already saved during REST creation and stays in the conversation.
          this._markConversationRejected(conversationId);
          if (messageId) {
            // Assistant message already exists — convert it into a moderation message
            try {
              useChatStore.getState().updateLocalChatMessage(conversationId, messageId, {
                isError: true,
                content: errorMessage || "I can't help with that request.",
                moderationMessage: errorMessage || "I can't help with that request.",
                buttons: [{ action: RESPONSE_ACTION_TYPES.CONTENT_MODERATION }],
                isWaiting: false,
                isStreaming: false,
                isThinking: false,
                isTyping: false,
              });
            } catch (e) {
              console.error('[Socket] trigger_conversation_socket_inference_error: Failed to update message as moderation:', e);
            }
          } else {
            // No assistant message yet — add a new moderation card
            try {
              this._addModerationMessage(conversationId, errorMessage);
            } catch (e) {
              console.error('[Socket] trigger_conversation_socket_inference_error: Failed to add moderation message:', e);
            }
          }
        } else {
          // Credit/Plus/model-access/generic errors: mark the message with error state
          const errorType = isModelAccessError
            ? (reason || 'model_plus_only')
            : isCreditError
              ? (reason || 'INSUFFICIENT_CREDITS')
              : isPlusError
                ? (reason || 'PLUS_REQUIRED')
                : reason || undefined;
          const updates = {isError: true, buttons: actions || undefined, errorType};
          if (!messageId) {
            updates.id = `error-${Crypto.randomUUID()}`;
          }
          useChatStore.getState().updateLocalChatMessage(conversationId, messageId, updates);
        }
      } catch (error) {
        console.error('[Socket] Error handling trigger_conversation_socket_inference_error:', error);
      }
    });

    // Handle user messages
    this.socket.on('message_reactions', ({ conversationId, messageId, reactions }) => {
      useChatStore.getState().updateLocalChatMessage(conversationId, messageId, { reactions });
    });

    this.socket.on('new_user_message', ({conversationId, message, clientMessageId}) => {
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      const localBubble = clientMessageId
        ? chat?.messages?.find(m => m.id === clientMessageId)
        : null;
      const existingByServerId = message?._id
        ? chat?.messages?.find(m => m.id === message._id || m._id === message._id)
        : null;

      // Build the update payload. Only include content from the backend if it
      // is non-empty — the optimistic user message already has the correct text
      // typed by the user, and we must not overwrite it with an empty/undefined
      // value that some fast-mode code paths may return.
      const updatePayload = {
        id: message._id,
        role: message.role,
        timestamp: message.date,
        isPending: false,
      };
      if (message.content) {
        updatePayload.content = message.content;
      }
      // Auto-mode attribution (`___isAutoMode`) lives on parameters. The
      // optimistic bubble may already have a client stamp; merge so the
      // server flag (and platform/timezone) land without dropping ___oneShotModel.
      if (message.parameters && typeof message.parameters === 'object') {
        updatePayload.parameters = mergeUserMessageParameters(
          (localBubble || existingByServerId)?.parameters,
          message.parameters,
        );
      }

      // Reconcile by clientMessageId when the server echoes it. An exact
      // match swaps our optimistic bubble's temp id for the server id. When
      // it matches no local bubble, the send came from another device (the
      // event broadcasts to the whole user room) — append it as a new message
      // instead of letting the heuristic corrupt an unrelated pending bubble.
      // Without an echo (older server), fall back to the latest-pending swap.
      // Work-mode "continue in chat" re-emits this event for an already-acked
      // user turn (no clientMessageId) so the ___workModeChoice stamp lands.
      let reconciled = false;
      if (clientMessageId) {
        if (localBubble) {
          chatStore.updateLocalChatMessage(conversationId, clientMessageId, updatePayload);
        } else if (chat && message?._id) {
          const alreadyThere = chat.messages?.some(m => m.id === message._id || m._id === message._id);
          if (!alreadyThere) {
            chatStore.addMessage(conversationId, {
              id: message._id,
              content: message.content,
              role: 'user',
              timestamp: message.date || new Date().toISOString(),
              parameters: message.parameters || undefined,
            });
          }
        }
        reconciled = true;
      }
      if (!reconciled && existingByServerId) {
        chatStore.updateLocalChatMessage(
          conversationId,
          existingByServerId.id || message._id,
          updatePayload,
        );
        reconciled = true;
      }
      if (!reconciled) {
        // Update the message first (swaps temp ID to server ID).
        chatStore.updateLocalChatMessage(conversationId, null, updatePayload);
      }

      // Then update chat details. updateLocalChatDetails now skips redundant
      // updates, so if isInferencing is already true this is a no-op and won't
      // create an extra re-render that causes the user message to flash.
      chatStore.updateLocalChatDetails(conversationId, {
        isInferencing: true,
        lastMessageTime: getRelativeTime(message.date)
      });
    });

    // Handle user message errors
    this.socket.on('user_message_error', ({conversationId, messageId, actions, failedAssistantMessageIds }) => {
      const chatStore = useChatStore.getState();
      clearStreamingDraftsForParent(conversationId, messageId);

      // Update chat details
      chatStore.updateLocalChatDetails(conversationId, {
        isInferencing: false,
        isInferencingWaiting: false
      });

      this._clearStreamingState(conversationId);
      const chatMessages = chatStore.chats.find(c => c.id === conversationId)?.messages || [];
      // Thinking-only / mid-stream failures previously cleared isThinking and
      // left an empty assistant bubble (error only on the user message + toast).
      // Finalize those turns into a visible assistant error or Continue state.
      this._finalizeStalledAssistantMessages(conversationId, {
        errorMessage: 'Something went wrong generating a response. Please try again.',
        toast: false,
      });

      // Flip failed media placeholders ("Generating image..." cards) into an
      // error state. Without this, isAwaitingRequestedMedia() keeps the
      // placeholder spinning forever — it only stands down when the assistant
      // message itself carries an error or the media arrives. The backend
      // sends the exact placeholder ids (an empty array means "no media
      // placeholders failed", e.g. a text-only failure — touch nothing).
      // The in-flight heuristic is ONLY for older payloads that omit the
      // field entirely, and even then only targets placeholders that were
      // actively in flight — an async media job still polling must not be
      // marked failed by an unrelated inference error.
      const hasExplicitFailedIds = Array.isArray(failedAssistantMessageIds);
      const failedIds = hasExplicitFailedIds ? failedAssistantMessageIds : [];
      const failedPlaceholders = chatMessages.filter(m =>
        m.role === 'assistant' && (
          failedIds.includes(m.id) ||
          (
            !hasExplicitFailedIds &&
            isAwaitingRequestedMedia(m) &&
            (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
          )
        )
      );
      failedPlaceholders.forEach(placeholder => {
        chatStore.updateLocalChatMessage(conversationId, placeholder.id, {
          isError: true,
          hasError: true,
          isThinking: false,
          isTyping: false,
          isWaiting: false,
          isStreaming: false,
        });
      });

      // Finalize skips media placeholders so their dedicated path can mark
      // failures; still clear thinking/waiting flags on any leftover media
      // cards that were not in the failed set (async jobs may continue).
      chatMessages.forEach(m => {
        if (
          m.role === 'assistant' &&
          isAwaitingRequestedMedia(m) &&
          !failedIds.includes(m.id) &&
          (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
        ) {
          chatStore.updateLocalChatMessage(conversationId, m.id, {
            isThinking: false,
            isTyping: false,
            isWaiting: false,
            isStreaming: false,
            inferenceStatus: undefined,
            inferenceStage: undefined,
            inferenceStatusTimestamp: undefined,
          });
        }
      });

      // Update message with error
      chatStore.updateLocalChatMessage(conversationId, messageId, {
        isError: true,
        buttons: actions || undefined
      });
    });

    // Handle new conversation created in another tab/window
    this.socket.on('conversation_created', (data) => {
      if (data.conversation) {
        const chatStore = useChatStore.getState();
        // Skip resurrecting a temp chat we just cleaned up on navigate-away
        // (common race on native when conversation_created arrives late).
        if (chatStore.wasTemporaryChatRecentlyDeleted(data.conversation.id)) {
          return;
        }
        chatStore.addChats([data.conversation]);
      }
    });

    // Handle conversations deleted in another tab/device so the sidebar stays
    // in sync without a manual refresh. The deleting client already removed
    // the rows locally, so this is a no-op there.
    this.socket.on('conversation_deleted', (data) => {
      const ids = Array.isArray(data?.conversationIds) ? data.conversationIds : [];
      if (ids.length === 0) return;
      const chatStore = useChatStore.getState();
      ids.forEach((id) => {
        if (chatStore.chats.some(c => c.id === id)) {
          chatStore.deleteChat(id);
        }
      });
    });

    // Handle conversation title updates
    this.socket.on('conversation_title', (data) => {
      const conversationId = data.conversationId || data.id;
      if (conversationId && data.title) {
        const cleanTitle = _sanitizeChatTitle(data.title);
        const chatStore = useChatStore.getState();
        chatStore.updateLocalChatDetails(conversationId, {
          title: cleanTitle,
        });
        // Work hub lists TaskSession.title, which the backend updates in
        // lockstep — keep the in-memory task row matching the sidebar.
        const coworker = useCoworkerStore.getState();
        const task = Object.values(coworker.tasks || {}).find((t) => (
          String(t.conversationId || t.conversation || '') === String(conversationId)
        ));
        if (task) coworker.addTask({ id: task.id, title: cleanTitle });
      }
    });

    this.socket.on('work_mode_choice', ({ conversationId, message }) => {
      if (!conversationId || !message) return;
      this._markConversationRejected(conversationId);
      this._clearStreamingState(conversationId);
      const store = useChatStore.getState();
      store.updateLocalChatMessage(conversationId, message.id || message._id, {
        parameters: message.parameters,
      });
      store.updateLocalChatDetails(conversationId, {
        isInferencing: false,
        isInferencingWaiting: false,
        pendingInferenceStatus: undefined,
        pendingInferenceStage: undefined,
        pendingCanFastAnswer: undefined,
        pendingInferenceStatusTimestamp: undefined,
      });
    });

    // The user accepted Work; switch the existing conversation to its Work renderer.
    this.socket.on('conversation_upgraded_to_work', ({ conversationId, taskId }) => {
      if (!conversationId || !taskId) return;
      const chatStore = useChatStore.getState();
      const existing = chatStore.chats.find((c) => c.id === conversationId);
      if (!existing) return;
      if (existing.coworkerSession && String(existing.coworkerSession) === String(taskId)) return;

      devLog('[Socket] conversation_upgraded_to_work', { conversationId, taskId });
      chatStore.updateLocalChatDetails(conversationId, {
        flags: { ...(existing.flags || {}), isCoworkerSession: true },
        coworkerSession: String(taskId),
        // Keep the run live for the composer and the Work liveness helpers.
        // isInferencingWaiting is deliberately left alone: the chat thinking
        // bubble holds its place through the handover fade, and ChatView
        // releases it at the trough so the Work activity line replaces it in
        // one move rather than leaving a gap (see useWorkModeHandover).
        isInferencing: true,
        // Drives the one-line explanation under the user's message. Session
        // state only: on a later visit the thread is simply a Work thread and
        // needs no note.
        wasUpgradedToWork: true,
      });
      // Seed the task so the Work header has a status before the first fetch
      // resolves, then let the store pull the real record.
      const coworker = useCoworkerStore.getState();
      coworker.addTask({
        id: String(taskId),
        status: 'running',
        conversationId: String(conversationId),
        // The chat bubble is already on screen; the live transcript must
        // drop the launch user_message until fetchTask confirms the flag.
        adoptedUserTurn: true,
      });
      coworker.fetchTask?.(String(taskId));
    });

    // Handle stream start
    this.socket.on('conversation_stream_start', ({ conversationId }) => {
      // Skip if conversation was recently rejected (e.g. content moderation).
      // This prevents late-arriving stream_start from re-enabling loading after an error.
      if (this._isConversationRecentlyRejected(conversationId)) {
        devLog('[Socket] conversation_stream_start: Ignoring — conversation was recently rejected', { conversationId });
        return;
      }

      const chatStore = useChatStore.getState();

      if (!chatStore.chats.find(c => c.id === conversationId)) {
        chatStore.beginInferencingConversation(conversationId);
      }
      
      // Mobile-specific: Clear the stream end timestamp so new streaming events are allowed
      this.streamEndTimestamps.delete(conversationId);
      // Clear streaming started flag and recovery state - new inference starting fresh
      this.streamingStarted.delete(conversationId);
      this.stallRecoveryAttempts.delete(conversationId);
      this._clearPreprocessingWatchdog(conversationId);

      // Clear inference status from all old messages when a new inference starts
      const chat = chatStore.chats.find(c => c.id === conversationId);
      if (chat?.messages) {
        chat.messages
          .filter(m => m.role === 'assistant' && (m.inferenceStatus || m.inferenceStage))
          .forEach(oldMessage => {
            chatStore.updateLocalChatMessage(conversationId, oldMessage.id, {
              inferenceStatus: undefined,
              inferenceStage: undefined,
              inferenceStatusTimestamp: undefined
            });
          });
      }

      // Clear follow-up suggestions when new inference starts
      chatStore.clearFollowUpSuggestions(conversationId);
      
      // Clear any pending inference status from previous inference and set waiting state
      chatStore.updateLocalChatDetails(conversationId, {
        isInferencing: true,
        isInferencingWaiting: true,
        pendingInferenceStatus: undefined,
        pendingInferenceStage: undefined,
        pendingCanFastAnswer: undefined,
        pendingInferenceStatusTimestamp: undefined,
      });

      // Mark preprocessing activity - watchdog not started yet (waiting for tokens)
      this._markPreprocessingActivity(conversationId);
    });

    // Handle new assistant messages
    this.socket.on('new_assistant_message', ({ conversationId, message, isRequestingMedia }) => {
      // Skip if conversation was recently rejected (e.g. content moderation)
      if (this._isConversationRecentlyRejected(conversationId)) {
        devLog('[Socket] new_assistant_message: Ignoring — conversation was recently rejected', { conversationId });
        return;
      }

      const chatStore = useChatStore.getState();
      
      // Debug: Log message creation
      devLog('[Socket] new_assistant_message:', {
        conversationId,
        messageId: message._id,
        isRequestingMedia,
        contentPreview: message.content?.substring(0, 50),
        parentMessageId: message.parentMessageId,
      });
      
      // Check if message already exists to prevent duplicates (can happen on reconnection)
      const chat = chatStore.chats.find(c => c.id === conversationId);
      const existingMessage = chat?.messages?.find(m => 
        m.id === message._id || m._id === message._id
      );
      
      if (existingMessage) {
        devLog('[Socket] new_assistant_message: Duplicate message ignored', message._id);
        // Just update the existing message's streaming state
        chatStore.updateLocalChatMessage(conversationId, existingMessage.id, {
          isWaiting: true,
          isStreaming: true
        });
        chatStore.updateLocalChatDetails(conversationId, {
          isInferencing: true,
          isInferencingWaiting: false
        });
        return;
      }
      
      // Use server timestamp if available, otherwise use client time
      const timestamp = message.date || message.timestamp || new Date().toISOString();
      
      // Transfer any pending inference status from chat level to the new message
      // This handles the case where inference_status events arrived before the message was created
      const pendingStatus = chat?.pendingInferenceStatus;
      const pendingStage = chat?.pendingInferenceStage;
      const pendingCanFastAnswer = chat?.pendingCanFastAnswer;
      const pendingTimestamp = chat?.pendingInferenceStatusTimestamp;
      
      chatStore.addMessage(conversationId, {
        id: message._id,
        role: 'assistant',
        modelIds: extractMessageModelIds(message),
        agentIds: message.agentIds,
        timestamp: timestamp,
        isRequestingMedia: isRequestingMedia || null, // image, video, audio, pdf, etc
        content: message.content,
        parentMessageId: message.parentMessageId,
        isWaiting: true,
        isStreaming: true,
        isThinking: true,
        // Transfer pending status if available
        inferenceStatus: pendingStatus || undefined,
        inferenceStage: pendingStage || undefined,
        inferenceStatusTimestamp: pendingTimestamp || undefined,
        canFastAnswer: pendingCanFastAnswer || false,
      });
      
      // Clear pending status from chat level now that message has it
      chatStore.updateLocalChatDetails(conversationId, {
        isInferencing: true,
        isInferencingWaiting: false,
        pendingInferenceStatus: undefined,
        pendingInferenceStage: undefined,
        pendingCanFastAnswer: undefined,
        pendingInferenceStatusTimestamp: undefined,
      });
    });

    // Handle token streaming
    this.socket.on('assistant_message_token_stream', ({ conversationId, messageId, content }) => {
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);

      // Activate watchdog on first token - streaming has started
      if (!this.streamingStarted.get(conversationId)) {
        this._markStreamingStarted(conversationId);
      } else {
        this._markStreamActivity(conversationId);
      }
      
      // Check if this conversation is still expecting streaming updates
      // This prevents late-arriving events from re-enabling streaming after stream_end
      if (chat && !chat.isInferencing && !chat.isInferencingWaiting) {
        devLog('[Socket] assistant_message_token_stream: Ignoring late event for non-inferencing conversation', {
          conversationId,
          messageId,
        });
        return;
      }
      
      // Mobile-specific: Check if we've received stream_end recently for this conversation
      // This handles out-of-order events that can occur during network instability
      const streamEndTime = this.streamEndTimestamps.get(conversationId);
      if (streamEndTime && (Date.now() - streamEndTime) < 5000) {
        devLog('[Socket] assistant_message_token_stream: Ignoring event received after recent stream_end', {
          conversationId,
          messageId,
          timeSinceStreamEnd: Date.now() - streamEndTime,
        });
        return;
      }
      
      // Merge with any pending buffered entry for this message so a thinking
      // event that arrived earlier in the same flush window doesn't get its
      // thinkingContent dropped. Once content has started, isThinking is
      // pinned to false — late-arriving thinking events should not flip the
      // UI back to "Thinking" and cause flashing.
      const tokenBufferKey = `${conversationId}_${messageId}`;
      const existingTokenBuffer = this.tokenStreamBuffer.get(tokenBufferKey);
      const streamingMessage = existingTokenBuffer?.parentMessageId
        ? null
        : chat?.messages?.find(message => message.id === messageId || message._id === messageId);
      this.tokenStreamBuffer.set(tokenBufferKey, {
        ...(existingTokenBuffer || {}),
        conversationId,
        messageId,
        parentMessageId: existingTokenBuffer?.parentMessageId || streamingMessage?.parentMessageId,
        content: content,
        isThinking: false,
        contentHasStreamed: true,
      });
      this._startTokenStreamFlusher();
      
      // Track last token time for debugging delay between last token and stream_end
      this.lastTokenTimestamps = this.lastTokenTimestamps || new Map();
      this.lastTokenTimestamps.set(conversationId, Date.now());
    });

    // Handle thinking stream
    this.socket.on('assistant_message_thinking_stream', ({ conversationId, messageId, content, thinkingDurationMs }) => {
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);

      // Activate watchdog on first thinking content - streaming has started
      if (!this.streamingStarted.get(conversationId)) {
        this._markStreamingStarted(conversationId);
      } else {
        this._markStreamActivity(conversationId);
      }
      
      // Check if this conversation is still expecting streaming updates
      // This prevents late-arriving events from re-enabling streaming after stream_end
      if (chat && !chat.isInferencing && !chat.isInferencingWaiting) {
        devLog('[Socket] assistant_message_thinking_stream: Ignoring late event for non-inferencing conversation', {
          conversationId,
          messageId,
        });
        return;
      }
      
      // Mobile-specific: Check if we've received stream_end recently for this conversation
      // This handles out-of-order events that can occur during network instability
      const streamEndTime = this.streamEndTimestamps.get(conversationId);
      if (streamEndTime && (Date.now() - streamEndTime) < 5000) {
        devLog('[Socket] assistant_message_thinking_stream: Ignoring event received after recent stream_end', {
          conversationId,
          messageId,
          timeSinceStreamEnd: Date.now() - streamEndTime,
        });
        return;
      }
      
      // Merge with any pending buffered entry so a content event in the same
      // flush window keeps both its content field and this thinkingContent.
      // If content has already streamed for this message (either in this
      // buffer batch or already applied to the message), keep isThinking
      // pinned to false so the header doesn't flash back to "Thinking".
      const thinkingBufferKey = `${conversationId}_${messageId}`;
      const existingThinkingBuffer = this.tokenStreamBuffer.get(thinkingBufferKey);
      const targetMessage = chat?.messages?.find(m => m.id === messageId);
      const contentAlreadyStreamed =
        (existingThinkingBuffer && existingThinkingBuffer.contentHasStreamed) ||
        (targetMessage && typeof targetMessage.content === 'string' && targetMessage.content.length > 0);

      this.tokenStreamBuffer.set(thinkingBufferKey, {
        ...(existingThinkingBuffer || {}),
        conversationId,
        messageId,
        thinkingContent: content,
        thinkingDurationMs: thinkingDurationMs || 0,
        isThinking: contentAlreadyStreamed ? false : true,
      });
      this._startTokenStreamFlusher();
    });

    // Handle inference status updates (heartbeat from backend during preprocessing)
    // Backend sends these every 2s until tokens start streaming to keep frontend in "thinking" state
    this.socket.on('inference_status', ({ conversationId, messageId, status, stage, timestamp, elapsedMs, canFastAnswer }) => {
      devLog('[Socket] inference_status received:', { status, stage, elapsedMs, canFastAnswer });

      // Skip if conversation was recently rejected (e.g. content moderation).
      // Late heartbeats can arrive after the error handler clears loading state.
      if (this._isConversationRecentlyRejected(conversationId)) {
        devLog('[Socket] inference_status: Ignoring — conversation was recently rejected', { conversationId });
        return;
      }

      // Heartbeats refresh activity. Once thinking/answer tokens have started,
      // also keep the stream stall watchdog alive (thinking keepalives bridge
      // long CoT gaps without requiring new reasoning chunks).
      if (this.streamingStarted.get(conversationId)) {
        this._markStreamActivity(conversationId);
      } else {
        this._markPreprocessingActivity(conversationId);
      }

      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      if (!chat) {
        devLog('[Socket] inference_status: Chat not found, but watchdog reset', { conversationId });
        return;
      }

      // Skip if conversation is no longer inferencing (error already cleared it)
      if (!chat.isInferencing && !chat.isInferencingWaiting) {
        devLog('[Socket] inference_status: Ignoring — conversation is not inferencing', { conversationId });
        return;
      }

      // Mid-stream retry: the upstream provider rate-limited the first attempt
      // mid-stream and the backend is re-executing with a fresh request into the
      // SAME messageId. The message here is already streaming partial content
      // from the dead stream, so the normal "only update waiting/thinking
      // messages" branch below would ignore it. Clear the partial text, drop any
      // buffered tokens for this message, and flip back to a thinking state so
      // the user sees "Retrying…" until the fresh token stream arrives.
      if ((stage === 'retry' || stage === 'fallback') && messageId) {
        const retryMessage = (chat.messages || []).find(m =>
          (m.id === messageId || m._id === messageId) && m.role === 'assistant'
        );
        if (retryMessage) {
          // Drop any not-yet-flushed token buffer entry so it can't re-pollute
          // the cleared bubble on the next flusher tick.
          this.tokenStreamBuffer.delete(`${conversationId}_${messageId}`);
          this.tokenStreamBuffer.delete(`${conversationId}_${messageId}_thinking`);
          clearStreamingDraft(conversationId, messageId);

          chatStore.updateLocalChatMessage(conversationId, retryMessage.id || retryMessage._id, {
            content: '',
            thinkingContent: '',
            inferenceStatus: status || 'Retrying…',
            inferenceStage: stage,
            inferenceStatusTimestamp: timestamp || Date.now(),
            inferenceElapsedMs: elapsedMs,
            isThinking: true,
            isWaiting: true,
            isStreaming: false,
            // Keep stopReason/citations consistent with a fresh start.
            stopReason: undefined,
            citations: [],
          });
          devLog('[Socket] inference_status: mid-stream retry — cleared partial content', { conversationId, messageId });
        }
        return;
      }

      // Find the target message - only update messages that are in waiting/thinking state
      // DO NOT update messages that are already streaming content (isStreaming without isWaiting/isThinking)
      // Inference status should only appear during the pre-processing/thinking phase
      let targetMessage = null;
      
      if (messageId && messageId.startsWith('activeResponse-')) {
        // For early stages with placeholder ID, find the most recent assistant message
        // that is in a waiting or thinking state (not just streaming)
        if (chat.messages) {
          const assistantMessages = chat.messages.filter(m => m.role === 'assistant');
          // Only find messages that are waiting or thinking (pre-processing phase)
          // Exclude messages that are only streaming (content phase)
          targetMessage = [...assistantMessages].reverse().find(m => 
            (m.isWaiting || m.isThinking) && !(m.isStreaming && !m.isWaiting && !m.isThinking)
          );
        }
      } else if (messageId) {
        // For UUID messageId, find the specific message and verify it's in waiting/thinking state
        targetMessage = chat.messages.find(m => 
          (m.id === messageId || m._id === messageId) &&
          m.role === 'assistant' &&
          (m.isWaiting || m.isThinking) &&
          !(m.isStreaming && !m.isWaiting && !m.isThinking) // Don't match messages that are only streaming
        );
      } else {
        // No messageId provided, find any message in waiting/thinking state
        targetMessage = chat.messages.find(m => 
          m.role === 'assistant' &&
          (m.isWaiting || m.isThinking) &&
          !(m.isStreaming && !m.isWaiting && !m.isThinking) // Don't match messages that are only streaming
        );
      }
      
      // Only update if we found a message in the waiting/thinking phase
      // This prevents applying status to messages that are already streaming content
      if (targetMessage) {
        const hasThinkingContent =
          typeof targetMessage.thinkingContent === 'string' &&
          targetMessage.thinkingContent.trim().length > 0;
        // Soft keepalive during CoT: refresh timestamps/status but do NOT flip
        // isWaiting back on — that alternated Thinking/Waiting on every tick
        // for reasoning models (Kimi / DeepSeek) and caused UI flicker.
        const isThinkingKeepalive =
          stage === 'thinking_keepalive' ||
          stage === 'thinking' ||
          hasThinkingContent;

        chatStore.updateLocalChatMessage(conversationId, targetMessage.id, {
          inferenceStatus: status,
          inferenceStage: stage,
          inferenceStatusTimestamp: timestamp || Date.now(),
          inferenceElapsedMs: elapsedMs,
          isThinking: true,
          isWaiting: isThinkingKeepalive ? false : true,
          canFastAnswer: canFastAnswer || false,
        });
      } else {
        // No message exists yet (still in isInferencingWaiting phase)
        // Store the status at the chat level so MessageWaiting can display it
        devLog('[Socket] inference_status: No target message, storing at chat level', { conversationId, status, stage });
        chatStore.updateLocalChatDetails(conversationId, {
          pendingInferenceStatus: status,
          pendingInferenceStage: stage,
          pendingCanFastAnswer: canFastAnswer || false,
          pendingInferenceStatusTimestamp: timestamp || Date.now(),
          pendingInferenceElapsedMs: elapsedMs,
        });
      }
    });

    // Handle image stream
    this.socket.on('assistant_message_image_stream', (data) => {
      const { conversationId, messageId, date, images } = data;
      const chatStore = useChatStore.getState();

      // Activate watchdog on first image - streaming has started
      if (!this.streamingStarted.get(conversationId)) {
        this._markStreamingStarted(conversationId);
      } else {
        this._markStreamActivity(conversationId);
      }
      
      // Debug: Log which message the image is being attached to
      const chat = chatStore.chats.find(c => c.id === conversationId);
      const targetMessage = chat?.messages?.find(m => m.id === messageId || m._id === messageId);
      devLog('[Socket] assistant_message_image_stream:', {
        conversationId,
        messageId,
        imageCount: images?.length,
        targetMessageFound: !!targetMessage,
        targetMessageContent: targetMessage?.content?.substring(0, 50),
        allMessageIds: chat?.messages?.map(m => m.id)?.slice(-5), // Last 5 message IDs
      });
      
      // Unlock the chat as soon as the image arrives. If we left
      // `isInferencing: true` here and the real `conversation_stream_end`
      // was suppressed earlier (by our image-in-flight guard) or never
      // fires at all, the chat would stay locked forever — the user
      // couldn't use the Regenerate button or send another message.
      //
      // There is a tiny window (~hundreds of ms) where the backend's
      // DB-level `conversation.flags.isInferencing` may still be `true`
      // because the orchestrator's finally block hasn't run yet. We
      // handle that case with an auto-retry in `emitRegenerateMessage`
      // / `emitSendUserMessage` on the `"already inferencing"` error.
      chatStore.updateLocalChatDetails(conversationId, {
        lastMessageTime: getRelativeTime(date),
        isInferencing: false,
        isInferencingWaiting: false,
        pendingInferenceStatus: undefined,
        pendingInferenceStage: undefined,
        pendingCanFastAnswer: undefined,
        pendingInferenceStatusTimestamp: undefined,
      });

      const result = chatStore.updateLocalChatMessage(conversationId, messageId, {
        images: images,
        isImage: true,
        isWaiting: false,
        isStreaming: false,
        isThinking: false,
        isTyping: false,
        timestamp: date,
        inferenceStatus: undefined,
        inferenceStage: undefined,
        inferenceStatusTimestamp: undefined,
      });

      // Reset watchdog timers so any late-arriving events don't retrigger
      // stall recovery on an already-completed generation.
      this._clearStreamingState(conversationId);
      this.streamEndTimestamps.set(conversationId, Date.now());

      if (!result) {
        devWarn('[Socket] assistant_message_image_stream: Failed to find message to update — refetching conversation', {
          messageId,
          conversationId,
        });
        chatsApi.getConversationById(conversationId)
          .then((data) => {
            if (data) useChatStore.getState().addChats([data]);
          })
          .catch((err) => {
            devWarn('[Socket] assistant_message_image_stream: Refetch failed', err?.message || err);
          });
      }
    });

    this.socket.on('cancel_user_conversation_inference_error', ({ conversationId, errorMessage }) => {
      // Error handled silently - UI already shows appropriate state
    });

    this.socket.on('cancel_user_conversation_inference_success', ({ conversationId }) => {
      const chatStore = useChatStore.getState();
      
      // Update chat details
      chatStore.updateLocalChatDetails(conversationId, {
        isInferencing: false,
        isInferencingWaiting: false,
      });

      this._clearStreamingState(conversationId);
      
      // Also stop any streaming messages - they won't receive conversation_stream_end
      const chat = chatStore.chats.find(c => c.id === conversationId);
      const streamingMessages = chat?.messages?.filter(m => 
        m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
      ) || [];
      
      if (streamingMessages.length > 0) {
        streamingMessages.forEach(streamingMessage => {
          chatStore.updateLocalChatMessage(conversationId, streamingMessage.id, {
            isThinking: false,
            isTyping: false,
            isWaiting: false,
            isStreaming: false,
            inferenceStatus: undefined,
            inferenceStage: undefined,
            inferenceStatusTimestamp: undefined
          });
        });
      }
    });

    // Handle memory created events (auto-save, explicit "remember X", dream worker)
    this.socket.on('memory_created', (data) => {
      const { memory } = data;

      // Validate required fields
      if (!memory || !memory.id) {
        console.error('[Memory] Invalid memory_created event:', data);
        return;
      }

      useMemoryStore.getState().invalidateWorkStarterPrompts();

      // Account Memory page only lists account-scoped memories.
      if (!memory.collectionId) {
        useMemoryStore.getState().addMemory(memory);
      }

      // Toast for interactive saves (explicit commands + post-turn auto-save)
      // with Undo; background dream-worker syncs update the store silently.
      const source = memory.source || (memory.isExplicitRequest ? 'explicit' : null);
      if (source === 'explicit' || source === 'auto') {
        const { addToast } = useToastStore.getState().actions;
        addToast(`✓ ${getTranslation('socket.memorySaved')} ${memory.content}`, 'success', [
          {
            title: getTranslation('socket.undo'),
            icon: 'rotate-left',
            action: RESPONSE_ACTION_TYPES.UNDO_MEMORY,
            data: { memoryId: memory.id },
          },
          {
            title: getTranslation('socket.viewMemories'),
            icon: 'brain',
            href: '/memory',
          },
        ]);
      }
    });

    // Handle memory updated events (auto-save/dream revising an existing memory)
    this.socket.on('memory_updated', (data) => {
      const { memory } = data;

      if (!memory || !memory.id) {
        console.error('[Memory] Invalid memory_updated event:', data);
        return;
      }

      useMemoryStore.getState().invalidateWorkStarterPrompts();
      if (!memory.collectionId) {
        useMemoryStore.getState().addMemory(memory);
      }

      const source = memory.source || null;
      if (source === 'explicit' || source === 'auto') {
        const { addToast } = useToastStore.getState().actions;
        addToast(`✓ ${getTranslation('socket.memoryUpdated')} ${memory.content}`, 'success', [
          {
            title: getTranslation('socket.viewMemories'),
            icon: 'brain',
            href: '/memory',
          },
        ]);
      }
    });

    // A save the user explicitly asked for did not happen. Without this the
    // chat path is silent and the user assumes "remember X" worked.
    this.socket.on('memory_save_failed', (data) => {
      const reason = data?.reason || 'save_failed';
      const { addToast } = useToastStore.getState().actions;

      if (reason === 'cap_reached') {
        addToast(getTranslation('socket.memoryCapReached'), 'error', [
          {
            title: getTranslation('socket.viewMemories'),
            icon: 'brain',
            href: '/memory',
          },
        ]);
        return;
      }

      const messageKey = {
        already_known: 'socket.memoryAlreadySaved',
        throttled: 'socket.memorySaveThrottled',
        nothing_to_save: 'socket.memoryNothingToSave',
        temporary_chat: 'socket.memoryTemporaryChat',
        collection_memory_off: 'socket.memoryCollectionOff',
      }[reason] || 'socket.memorySaveFailed';
      addToast(getTranslation(messageKey), 'default');
    });

    // Handle memory deleted events
    this.socket.on('memory_deleted', (data) => {
      const { memory, messageId, conversationId } = data;
      
      // Validate required fields
      if (!memory || !memory.id) {
        console.error('[Memory] Invalid memory_deleted event:', data);
        return;
      }
      
      // Remove memory from store
      useMemoryStore.getState().removeMemory(memory.id);
      useMemoryStore.getState().invalidateWorkStarterPrompts();
      
      // Show deletion notification
      const { addToast } = useToastStore.getState().actions;
      addToast(`${getTranslation('socket.memoryDeleted')} ${memory.content}`, 'default');
    });

    // Dream worker retired a memory (superseded, consolidated, or expired).
    // Silent store sync: the Memory page drops it without a refetch, no toast.
    this.socket.on('memory_archived', (data) => {
      const { memory } = data || {};
      if (!memory || !memory.id) {
        console.error('[Memory] Invalid memory_archived event:', data);
        return;
      }
      useMemoryStore.getState().removeMemory(memory.id);
      useMemoryStore.getState().invalidateWorkStarterPrompts();
    });

    // Dream worker revised the account profile overview shown at the top of
    // the Memory page. Patch the signed-in user's preferences in place.
    this.socket.on('memory_profile_updated', (data) => {
      const profileSummary = typeof data?.profileSummary === 'string' ? data.profileSummary.trim() : '';
      if (!profileSummary) return;
      const auth = useAuthStore.getState();
      if (!auth.user || typeof auth.updateUserPreferences !== 'function') return;
      auth.updateUserPreferences({ memory: { profileSummary, profileSummaryUserEdited: false } });
      useMemoryStore.getState().invalidateWorkStarterPrompts();
    });

    // Handle follow-up suggestions (arrives after inference completes)
    this.socket.on('follow_up_suggestions', (data) => {
      const { conversationId, messageId, suggestions, timestamp } = data;

      // Validate required fields
      if (!conversationId || !Array.isArray(suggestions)) {
        return;
      }

      // Store suggestions in chat store
      const chatStore = useChatStore.getState();
      chatStore.setFollowUpSuggestions(conversationId, {
        messageId,
        suggestions,
        timestamp: timestamp || Date.now()
      });
    });

    // Handle "Move this chat to <collection>?" suggestions (arrives after the
    // collection suggestion post-processor finds a confident match)
    this.socket.on('collection_suggestion', (data) => {
      const { conversationId, collectionId, collectionTitle, timestamp } = data;

      if (!conversationId || !collectionId) {
        return;
      }

      useChatStore.getState().setCollectionSuggestion(conversationId, {
        collectionId,
        collectionTitle: collectionTitle || '',
        timestamp: timestamp || Date.now(),
      });
    });

    // Handle card expiration warning (sent shortly after socket connection for Plus users)
    // This warning should only be shown once per day after a successful inference
    this.socket.on('card_expiration_warning', (data) => {
      const { daysRemaining, isExpired, expirationDate, cardLast4, cardType } = data;
      devLog('[Socket] card_expiration_warning received:', { daysRemaining, isExpired, expirationDate });
      
      // Store warning data in auth store
      // The actual display logic (once per day) is handled in the chat page
      const authStore = useAuthStore.getState();
      authStore.setCardExpirationWarning({
        daysRemaining,
        isExpired,
        expirationDate,
        cardLast4,
        cardType,
        receivedAt: Date.now(),
      });
    });

    // Voice mode media generation events
    // These fire when the AI triggers media generation during voice conversations
    this.socket.on('voice_media_started', (data) => {
      const { sessionId, type, prompt, status, timestamp } = data;
      devLog('[Socket] voice_media_started:', { type, prompt });
      
      // Only process if voice mode is active
      const voiceModeStore = useVoiceModeStore.getState();
      if (!voiceModeStore.isActive) {
        devLog('[Socket] voice_media_started ignored - voice mode not active');
        return;
      }
      
      // Update media generation state
      voiceModeStore.actions.setMediaStarted(type, prompt);
      devLog('[Socket] setMediaStarted called, mediaItems:', voiceModeStore.mediaItems?.length);
    });

    this.socket.on('voice_media_progress', (data) => {
      const { sessionId, type, progress, message, timestamp } = data;
      
      // Only process if voice mode is active
      const voiceModeStore = useVoiceModeStore.getState();
      if (!voiceModeStore.isActive) return;
      
      // Update progress state
      voiceModeStore.actions.setMediaProgress(progress, message);
    });

    this.socket.on('voice_media_complete', (data) => {
      const { sessionId, type, media, status, timestamp } = data;
      devLog('[Socket] voice_media_complete:', { type, mediaUrl: media?.url });
      
      // Only process if voice mode is active
      const voiceModeStore = useVoiceModeStore.getState();
      if (!voiceModeStore.isActive) {
        devLog('[Socket] voice_media_complete ignored - voice mode not active');
        return;
      }
      
      // Update with completed media
      voiceModeStore.actions.setMediaComplete(type, media);
      devLog('[Socket] setMediaComplete called, mediaItems now:', useVoiceModeStore.getState().mediaItems?.length);
    });

    this.socket.on('voice_media_error', (data) => {
      const { sessionId, type, error, reason, actions, credits, status, timestamp } = data;
      devLog('[Socket] voice_media_error:', { type, error, reason, actions, credits });
      
      // Only process if voice mode is active
      const voiceModeStore = useVoiceModeStore.getState();
      if (!voiceModeStore.isActive) return;
      
      // Update media item with error state
      voiceModeStore.actions.setMediaError(error);
      
      // Detect error type - prefer explicit reason from backend
      const {
        isModelAccessError,
        isCreditError,
        isPlusError,
      } = classifyResponseError({
        actions,
        reason,
        errorMessage: typeof error === 'string' ? error : error?.message,
      });
      
      // Map media type to display format
      const mediaTypeMap = {
        'create-video': 'video',
        'create-image': 'image',
        'create-music': 'music',
        'image-to-video': 'video',
        'video': 'video',
        'image': 'image',
        'music': 'music',
      };
      const mediaType = mediaTypeMap[type] || 'media';
      
      // Import modal store and show appropriate modal
      const { useModalStore: getModalStore } = require('@/store');
      const modalStore = getModalStore.getState();
      
      if (isModelAccessError) {
        openModelAccessPaywall(modalStore.actions.openModal, {
          isVideo: mediaType === 'video',
          title: 'Unlock this model',
          subtitle: typeof error === 'string' ? error : 'To use this model, buy credits or upgrade to Plus.',
        });
      } else if (isPlusError) {
        // Show Plus required modal
        modalStore.actions.openModal('VoiceMediaErrorModal', {
          errorType: 'PLUS_REQUIRED',
          mediaType,
          errorMessage: typeof error === 'string' ? error : undefined,
        });
      } else if (isCreditError) {
        // Show credits error modal with credit info
        modalStore.actions.openModal('VoiceMediaErrorModal', {
          errorType: 'INSUFFICIENT_CREDITS',
          mediaType,
          errorMessage: typeof error === 'string' ? error : undefined,
          creditsAvailable: credits?.available,
          creditsRequired: credits?.required,
        });
        
        // Refresh credits to ensure UI has latest values
        try {
          const authStore = useAuthStore.getState();
          if (authStore?.fetchUserCredits) {
            authStore.fetchUserCredits();
          }
        } catch (e) {
          // Silently fail - credit refresh is best effort
        }
      } else {
        // Show generic error modal for other errors (GENERATION_FAILED, etc.)
        modalStore.actions.openModal('VoiceMediaErrorModal', {
          errorType: 'GENERIC',
          mediaType,
          errorMessage: typeof error === 'string' ? error : 'Generation failed. Please try again.',
        });
      }
    });

    // Voice processing state events - fired by backend when LLM processing starts/ends
    // These events control the processing sound and state transitions for voice mode
    this.socket.on('voice_processing_started', (data) => {
      const { sessionId, transcript, conversationId, status, timestamp } = data;
      devLog('[Socket] voice_processing_started:', { sessionId, transcriptLength: transcript?.length });
      
      // Only process if voice mode is active
      const voiceModeStore = useVoiceModeStore.getState();
      if (!voiceModeStore.isActive) {
        devLog('[Socket] voice_processing_started ignored - voice mode not active');
        return;
      }
      
      // State is now managed by voiceSocket.js via the /voice namespace
      // Just update state here, sound is triggered by voiceSocket state handler
      voiceModeStore.actions.setState('processing');
    });

    this.socket.on('voice_processing_complete', (data) => {
      const { sessionId, transcript, response, durationMs, conversationId, status, timestamp } = data;
      devLog('[Socket] voice_processing_complete:', { sessionId, durationMs, responseLength: response?.length });
      
      // Only process if voice mode is active
      const voiceModeStore = useVoiceModeStore.getState();
      if (!voiceModeStore.isActive) {
        devLog('[Socket] voice_processing_complete ignored - voice mode not active');
        return;
      }
      
      // State is now managed by voiceSocket.js via the /voice namespace
      // Just update state here, sound is triggered by voiceSocket state handler
      voiceModeStore.actions.setState('speaking');
    });

    // Voice transcript saved - fired in real-time as each transcript message is saved
    // This allows messages to appear in the conversation immediately during voice mode
    this.socket.on('voice_transcript_saved', (data) => {
      const { conversationId, message, sessionId, timestamp } = data;
      devLog('[Socket] voice_transcript_saved:', { conversationId, role: message?.role, contentLength: message?.content?.length });
      
      if (!conversationId || !message) {
        devLog('[Socket] voice_transcript_saved - invalid data, skipping');
        return;
      }
      
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      
      if (!chat) {
        devLog('[Socket] voice_transcript_saved - conversation not found:', conversationId);
        return;
      }
      
      // Get message ID (handle both id and _id from backend)
      const messageId = message.id || message._id;
      
      // Skip empty content
      if (!message.content || !message.content.trim()) return;
      
      // Check if this message already exists
      const existingMessage = chat.messages?.find(m => 
        (messageId && (m.id === messageId || m._id === messageId)) ||
        (m.role === message.role && m.content === message.content)
      );
      
      if (existingMessage) {
        // If the existing message has a temp voice ID but we now have the real server ID,
        // update the temp ID to the real one. This prevents ID mismatches that cause
        // duplicates when addChats merges backend data with local messages.
        if (messageId && existingMessage.id !== messageId && existingMessage.id?.startsWith?.('voice-')) {
          devLog('[Socket] voice_transcript_saved - updating temp voice ID to real ID:', existingMessage.id, '->', messageId);
          chatStore.updateLocalChatMessage(conversationId, existingMessage.id, {
            id: messageId,
          });
        } else {
          devLog('[Socket] voice_transcript_saved - message already exists, skipping');
        }
        return;
      }
      
      // Add the new message to the chat
      chatStore.addMessage(conversationId, {
        id: messageId || `voice-${sessionId}-${Date.now()}`,
        role: message.role === 'user' ? 'user' : 'assistant',
        content: message.content,
        timestamp: message.timestamp || timestamp || new Date().toISOString(),
        isVoiceMessage: true,
        parameters: message.parameters || { source: 'voice' },
      });
      
      devLog('[Socket] voice_transcript_saved - added message to conversation');
      
      // Trigger title generation after enough conversation has happened
      // Wait for at least 2 exchanges (2 user + 2 assistant messages) before generating title
      // This ensures we have enough context for a meaningful title, not just "Greeting"
      if (message.role === 'assistant') {
        const refreshedChat = chatStore.chats.find(c => c.id === conversationId);
        const currentTitle = refreshedChat?.title?.toLowerCase() || '';
        const needsTitle = !refreshedChat?.title || 
          currentTitle === 'voice conversation' || 
          currentTitle === 'untitled chat' ||
          currentTitle === 'voice chat';
        
        if (needsTitle) {
          // Count messages to ensure we have enough content for a good title
          const messages = refreshedChat?.messages || [];
          const userMessageCount = messages.filter(m => m.role === 'user').length;
          const assistantMessageCount = messages.filter(m => m.role === 'assistant').length;
          
          // Wait for at least 2 exchanges before generating title
          if (userMessageCount >= 2 && assistantMessageCount >= 2) {
            const userId = useAuthStore.getState().user?.id;
            if (userId) {
              devLog('[Socket] voice_transcript_saved - triggering title generation (enough exchanges)');
              // Small delay to ensure message is saved on backend first
              setTimeout(() => {
                chatStore.retryTitleRetrieval(userId, conversationId);
              }, 500);
            }
          }
        }
      }
    });

    // Voice transcript complete - fired after voice call ends with full conversation history
    // This is a fallback to ensure all messages are present; real-time messages are added via voice_transcript_saved
    this.socket.on('voice_transcript_complete', (data) => {
      const { conversationId, transcript, sessionId, timestamp } = data;
      devLog('[Socket] voice_transcript_complete:', { conversationId, messageCount: transcript?.length });
      
      if (!conversationId || !Array.isArray(transcript) || transcript.length === 0) {
        devLog('[Socket] voice_transcript_complete - invalid data, skipping');
        return;
      }
      
      // Update the chat store with the transcript messages
      const chatStore = useChatStore.getState();
      
      // Re-fetch the chat to get the most current state
      let chat = chatStore.chats.find(c => c.id === conversationId);
      
      if (!chat) {
        devLog('[Socket] voice_transcript_complete - conversation not found:', conversationId);
        return;
      }
      
      let messagesAdded = 0;
      let messagesUpdated = 0;
      
      // Process each transcript entry and add/update messages
      transcript.forEach((entry, index) => {
        const { role, content, timestamp: entryTimestamp, messageId } = entry;
        const normalizedRole = role === 'user' ? 'user' : 'assistant';
        
        // Skip empty content
        if (!content || !content.trim()) return;
        
        // Re-fetch chat to get current messages (may have been updated by previous iterations)
        chat = chatStore.chats.find(c => c.id === conversationId);
        
        // Check if this message already exists:
        // 1. By exact messageId match
        // 2. By content + role match (for optimistic messages that may have temp IDs)
        const existingMessage = chat?.messages?.find(m => 
          (messageId && (m.id === messageId || m._id === messageId)) ||
          (m.role === normalizedRole && m.content === content)
        );
        
        if (existingMessage) {
          // If message exists with temp ID (voice-*) and we have a real messageId, update it
          if (messageId && existingMessage.id?.startsWith?.('voice-') && existingMessage.id !== messageId) {
            chatStore.updateLocalChatMessage(conversationId, existingMessage.id, {
              id: messageId,
            });
            messagesUpdated++;
          }
          return;
        }
        
        // Add the new message from transcript
        chatStore.addMessage(conversationId, {
          id: messageId || `voice-complete-${sessionId}-${index}-${Date.now()}`,
          role: normalizedRole,
          content: content,
          timestamp: entryTimestamp || timestamp || new Date().toISOString(),
          isVoiceMessage: true,
          parameters: { source: 'voice' },
        });
        messagesAdded++;
      });
      
      // Update chat's last message time
      chatStore.updateLocalChatDetails(conversationId, {
        lastMessageTime: getRelativeTime(timestamp || new Date().toISOString()),
      });
      
      devLog('[Socket] voice_transcript_complete - processed', transcript.length, 'entries:', {
        added: messagesAdded,
        updated: messagesUpdated,
        skipped: transcript.length - messagesAdded - messagesUpdated,
      });
      
      // Trigger title generation for voice conversations
      // Voice chats start with generic titles like "Voice Conversation" - generate a real title
      // once we have enough transcript content for a meaningful title
      const refreshedChat = chatStore.chats.find(c => c.id === conversationId);
      const currentTitle = refreshedChat?.title?.toLowerCase() || '';
      const needsTitle = !refreshedChat?.title || 
        currentTitle === 'voice conversation' || 
        currentTitle === 'untitled chat' ||
        currentTitle === 'voice chat';
      
      if (needsTitle) {
        // Count messages in transcript to ensure meaningful content
        const userMessages = transcript.filter(t => t.role === 'user').length;
        const assistantMessages = transcript.filter(t => t.role === 'assistant').length;
        
        // Need at least 1 full exchange (user + assistant) with some substance
        // Lower threshold than real-time since this is end of session
        if (userMessages >= 1 && assistantMessages >= 1) {
          const userId = useAuthStore.getState().user?.id;
          if (userId) {
            devLog('[Socket] voice_transcript_complete - triggering title generation');
            // Delay slightly to ensure all messages are saved on backend
            setTimeout(() => {
              chatStore.retryTitleRetrieval(userId, conversationId);
            }, 1000);
          }
        }
      }
    });

    // Handle file processing complete event
    // This event is emitted by the backend when a file (PDF, document, etc.) finishes processing
    this.socket.on('file_processing_complete', (data) => {
      const { fileId, status, fileType, error, timestamp } = data;
      
      devLog('[Socket] file_processing_complete received:', {
        fileId,
        status,
        fileType,
        hasError: !!error,
      });
      
      if (!fileId) {
        devWarn('[Socket] file_processing_complete: Missing fileId');
        return;
      }
      
      const fileStore = useFileStore.getState();
      const chatInputStore = useChatInputStore.getState();
      const existingFile = fileStore.files[fileId];
      
      // Prepare the processing status update
      let processingStatusUpdate;
      
      if (status === 'completed') {
        processingStatusUpdate = {
          processingStatus: {
            ...(existingFile?.processingStatus || {}),
            status: 'completed',
            completedAt: timestamp || Date.now(),
          }
        };
        
        // Update file in the file store
        if (existingFile) {
          fileStore.actions.updateFileInStore(fileId, processingStatusUpdate);
        }
        
        // Update file in the chat input store (for UI that shows "Analyzing..." state)
        chatInputStore.updateFileById(fileId, processingStatusUpdate);
        
        devLog('[Socket] file_processing_complete: File marked as completed', { fileId, fileType });
      } else if (status === 'failed') {
        processingStatusUpdate = {
          processingStatus: {
            ...(existingFile?.processingStatus || {}),
            status: 'failed',
            error: error || 'Processing failed',
            failedAt: timestamp || Date.now(),
          }
        };
        
        // Update file in the file store
        if (existingFile) {
          fileStore.actions.updateFileInStore(fileId, processingStatusUpdate);
        }
        
        // Update file in the chat input store (for UI that shows "Analyzing..." state)
        chatInputStore.updateFileById(fileId, processingStatusUpdate);
        
        // Show error toast to user
        const { addToast } = useToastStore.getState().actions;
        const fileName = existingFile?.name || existingFile?.originalName || 'File';
        addToast(`${getTranslation('socket.fileProcessFailed')} ${fileName}: ${error || 'Unknown error'}`, 'error');
        
        devLog('[Socket] file_processing_complete: File processing failed', { fileId, fileType, error });
      }
    });

    // Handle fast mode toggled confirmation from server
    // Note: We optimistically update the UI when emitting, so this is just a confirmation
    this.socket.on('fastModeToggled', (data) => {
      const { conversationId, fastMode } = data;
      console.log('[Socket] fastModeToggled received (server confirmation):', { conversationId, fastMode, data });
      
      if (!conversationId) {
        console.warn('[Socket] fastModeToggled: Missing conversationId');
        return;
      }
      
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      
      console.log('[Socket] fastModeToggled: Syncing with server state', { 
        chatFound: !!chat, 
        currentFlags: chat?.flags,
        serverFastMode: fastMode 
      });
      
      // Sync with server state (in case optimistic update was wrong)
      chatStore.updateLocalChatDetails(conversationId, {
        flags: {
          ...(chat?.flags || {}),
          fastMode: fastMode,
        }
      });
    });

    // Handle fast mode toggle errors from server
    this.socket.on('fastModeError', (data) => {
      const { conversationId, error } = data;
      console.error('[Socket] fastModeError received:', { conversationId, error, data });
      
      if (!conversationId) {
        console.warn('[Socket] fastModeError: Missing conversationId');
        return;
      }
      
      const chatStore = useChatStore.getState();
      const chat = chatStore.chats.find(c => c.id === conversationId);
      
      // Revert the optimistic update by toggling back
      // Since we don't know the previous state, we'll set it to the opposite of what we tried
      if (chat) {
        const currentFastMode = chat?.flags?.fastMode || false;
        chatStore.updateLocalChatDetails(conversationId, {
          flags: {
            ...(chat?.flags || {}),
            fastMode: !currentFastMode, // Revert to opposite
          }
        });
      }
      
      // Show error toast
      const { addToast } = useToastStore.getState().actions;
      addToast(error || getTranslation('socket.fastModeError'), 'error');
    });

    // Handle referral credit earned (fired when a referred user subscribes to Plus)
    this.socket.on('referral_credit', (data) => {
      devLog('[Socket] referral_credit received:', data);
      const { addToast } = useToastStore.getState().actions;
      const credits = data?.credits || 250;
      addToast(`🎉 ${getTranslation('socket.referralCredit')} ${credits} ${getTranslation('socket.referralCreditSuffix')}`, 'success');
      const authStore = useAuthStore.getState();
      authStore.fetchUserCredits?.({ force: true }).catch(() => {});
    });

    this.socket.on('earn_credited', (data) => {
      devLog('[Socket] earn_credited received:', data);
      const { addToast } = useToastStore.getState().actions;
      const credits = data?.credits || 0;
      const description = data?.description || 'Earned credits';
      addToast(`+${credits} credits — ${description}`, 'earn');
      const authStore = useAuthStore.getState();
      if (authStore.fetchUserCredits) {
        authStore.fetchUserCredits({ force: true }).catch(() => {});
      }
      try {
        require('@/store/earnProgramsRefresh').useEarnProgramsRefreshStore.getState().bumpEarnPrograms();
      } catch (_) { /* no-op */ }
    });

    // A Work run outlives the screen that started it — the user hands over a
    // goal, walks away, and this is the only thing that tells them it landed.
    this.socket.on('task:update', async (data) => {
      const taskId = data?.id ? String(data.id) : null;
      if (!taskId) return;
      devLog('[Socket] task:update received:', data);

      const coworker = useCoworkerStore.getState();
      const known = coworker.tasks[taskId];
      const creditsUsed = Number(data.creditsUsed);
      if (known) {
        coworker.addTask({
          id: taskId,
          ...(data.status ? { status: data.status } : {}),
          ...(Number.isFinite(data.spentTokens)
            ? { budget: { ...known.budget, spentTokens: data.spentTokens } }
            : {}),
          ...(Number.isFinite(creditsUsed) ? { creditsUsed: Math.round(creditsUsed) } : {}),
          ...(Array.isArray(data.followUpQueue) ? { followUpQueue: data.followUpQueue } : {}),
        });
        if (Array.isArray(data.followUpQueue)) coworker.setFollowUpQueue(taskId, data.followUpQueue);
      }

      // The run footer shows creditsUsed, but the sidebar balance is
      // user.credits — refresh it or Plus members keep seeing the pre-run total.
      if (Number.isFinite(creditsUsed) && creditsUsed > 0) {
        const authStore = useAuthStore.getState();
        authStore.fetchUserCredits?.({ force: !isWorkActive(data.status) })?.catch?.(() => {});
      }

      // Still working, or already settled when we last looked: nothing to say.
      // Credits still merge above — a just-finished thread is already idle
      // in the store by the time settlement emits the billed total.
      if (isWorkActive(data.status)) {
        // A follow-up in the same task deserves its own completion alert.
        for (const key of this._notifiedWorkSettlements) {
          if (key.startsWith(`${taskId}:`)) this._notifiedWorkSettlements.delete(key);
        }
        return;
      }
      if (known && !isWorkActive(known.status)) return;

      const settlementKey = `${taskId}:${String(data.status || '')}`;
      if (this._notifiedWorkSettlements.has(settlementKey)) return;
      this._notifiedWorkSettlements.add(settlementKey);
      if (this._notifiedWorkSettlements.size > 256) {
        this._notifiedWorkSettlements.delete(this._notifiedWorkSettlements.values().next().value);
      }

      const settled = (known?.title && known?.conversation)
        ? known
        : await coworker.fetchTask(taskId).catch(() => null);
      const title = settled?.title || 'Work';

      // The thread they are reading shows the run settle on its own.
      const viewedFromPath = typeof window === 'undefined'
        ? null
        : window.location.pathname.match(/\/c\/([^/]+)/)?.[1];
      const viewing = viewedFromPath || useRouteStore.getState().currentLibraryId || null;
      const conversation = settled?.conversation || settled?.conversationId || null;
      const viewingThisThread = !!(viewing && conversation && String(viewing) === String(conversation));

      if (!viewingThisThread) {
        const { addToast } = useToastStore.getState().actions;
        if (data.status === 'idle') addToast(`Work finished — ${title}`, 'success');
        else if (data.status === 'failed') addToast(`Work couldn't finish — ${title}`, 'error');
        else if (data.status === 'budget_exceeded') addToast(`Work paused to make more room — ${title}`, 'info');
        else if (data.status === 'interrupted') addToast(`Work paused — ${title}`, 'info');
      }
      signalWorkTabAttention({
        kind: data.status,
        threadTitle: title,
        taskId,
        conversationId: conversation,
      });
    });

    // Handle stream end
    this.socket.on('conversation_stream_end', ({ conversationId, data, actions, hasError, error, errorMessage }) => {
      const chatStore = useChatStore.getState();
      const streamEndedWithError = !!(hasError || error || errorMessage);
      
      // Calculate time since last token for debugging
      const lastTokenTime = this.lastTokenTimestamps?.get(conversationId);
      const timeSinceLastToken = lastTokenTime ? Date.now() - lastTokenTime : null;
      // Clean up the timestamp after reading
      this.lastTokenTimestamps?.delete(conversationId);
      
      // Log detailed info about each message for debugging
      data?.forEach((item, index) => {
        const msg = item?.message;
        devLog(`[Socket] conversation_stream_end - message ${index + 1}/${data.length}:`, {
          messageId: msg?._id,
          role: msg?.role,
          modelIds: msg?.models?.map(m => m?.id || m?.slug || m),
          hasImages: msg?.images?.length > 0,
          imageCount: msg?.images?.length || 0,
          firstImageUrl: msg?.images?.[0]?.url?.substring(0, 80),
          hasFiles: msg?.files?.length > 0,
          fileCount: msg?.files?.length || 0,
          contentLength: msg?.content?.length || 0,
        });
      });
      
      devLog('[Socket] conversation_stream_end:', {
        conversationId,
        dataLength: data?.length,
        hasError: streamEndedWithError,
        errorMessage: errorMessage?.substring?.(0, 50),
      });

      if (streamEndedWithError) {
        this._restorePendingContinuation(conversationId);
      } else {
        this.pendingContinuationMessages.delete(conversationId);
      }

      // Always run normal teardown on stream_end. The composer must unlock
      // when the backend says the inference is over, regardless of image
      // model specifics.
      this._clearStreamingState(conversationId);
      // The final payload is authoritative for the entire turn, including
      // failed models omitted from `data`.
      clearStreamingDraft(conversationId);

      // Mobile-specific: Record the stream end timestamp to prevent stale events from reactivating streaming
      this.streamEndTimestamps.set(conversationId, Date.now());

      // Clean up old timestamps (keep only last 5 minutes worth)
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      for (const [convId, timestamp] of this.streamEndTimestamps.entries()) {
        if (timestamp < fiveMinutesAgo) {
          this.streamEndTimestamps.delete(convId);
        }
      }

      chatStore.updateLocalChatDetails(conversationId, {
        isInferencing: false,
        isInferencingWaiting: false,
        lastMessageTime: data?.length > 0 ? getRelativeTime(data[data.length - 1].message?.date) : undefined,
        // Clear any pending inference status
        pendingInferenceStatus: undefined,
        pendingInferenceStage: undefined,
        pendingCanFastAnswer: undefined,
        pendingInferenceStatusTimestamp: undefined,
      });

      const foundMessageIds = [];
      // Also track which frontend message IDs were updated (handles ID mismatches)
      const updatedFrontendIds = [];
      let hadMissingMessageId = false;

      if (data?.length > 0) {
        // Update all messages that were returned
        data.forEach(item => {
          const messageId = item?.message?._id || item?.message?.id;
          if (messageId) {
            foundMessageIds.push(messageId);
            const messageRole = item?.message?.role?.toLowerCase?.() || '';
            const isAssistantMsg = messageRole === 'assistant';
            const isUserMessage = messageRole !== 'assistant';
            const serializedMessage = {
              ...item.message,
              // actions: actions || undefined,
              isThinking: false,
              isTyping: false,
              isWaiting: false,
              isStreaming: false,
              // Propagate error state from the stream_end event to assistant messages only.
              // User messages should never be marked as errors.
              ...(streamEndedWithError && isAssistantMsg ? { isError: true } : {}),
              ...(errorMessage && isAssistantMsg && !item.message?.content ? { content: errorMessage } : {}),
              // Propagate truncation metadata so the UI can offer "Continue generating"
              isTruncated: item.message?.isTruncated || item.message?.stopReason === 'max_tokens' || item.message?.stopReason === 'length',
              stopReason: item.message?.stopReason || null,
            };
            const mappedMessage = mapSerializedMessageToMessage(serializedMessage);
            if (isAssistantMsg && messageId) {
              clearStreamingDraft(conversationId, messageId);
            }
            if (isUserMessage) {
              mappedMessage.isPending = false;
            }
            // Clear inference status when stream ends
            mappedMessage.inferenceStatus = undefined;
            mappedMessage.inferenceStage = undefined;
            mappedMessage.inferenceStatusTimestamp = undefined;
            
            // For user messages, preserve the original modelIds and actions from the UI selection
            // The backend may return different modelIds due to auto-routing (e.g., detecting
            // "draw me a cat" and using image-generator), but we want to keep the user's
            // original selection in the UI
            // Similarly, the backend may auto-detect and add actions like 'search-web', but
            // we preserve the user's original explicit actions so the toggle reflects their choice
            if (isUserMessage) {
              // Get fresh state to ensure we have the latest message data
              const freshChatStore = useChatStore.getState();
              const chat = freshChatStore.chats.find(c => c.id === conversationId);
              const existingMessage = chat?.messages?.find(m => m.id === messageId || m._id === messageId);
              
              // Preserve the user's original message content if the backend
              // returned empty/undefined content.  The optimistic add already
              // stored the correct text; overwriting it would make the message
              // invisible (shouldDisplayMessage → false).
              mappedMessage.content = pickAuthoritativeMessageText(
                existingMessage?.content,
                mappedMessage.content
              );
              
              // Preserve original modelIds (user's explicit model selection)
              // The backend may auto-route to different models (e.g., image-generator),
              // but we want to keep the user's original selection
              // Note: Empty array [] means "auto mode" and should also be preserved
              if (Array.isArray(existingMessage?.modelIds)) {
                mappedMessage.modelIds = existingMessage.modelIds;
              }
              
              // Preserve original agentIds (user's explicit agent selection)
              // The backend may auto-route to different agents, but we want to keep
              // the user's original selection
              // Note: Empty array [] means "no agent selected" and should also be preserved
              if (Array.isArray(existingMessage?.agentIds)) {
                mappedMessage.agentIds = existingMessage.agentIds;
              }
              
              // Preserve original actions if they exist (user's explicit toggle state)
              // The backend may add auto-detected actions like 'search-web', but we want to
              // keep the user's original selection so the UI toggle reflects their explicit choice
              // Note: Empty array [] should also be preserved (user explicitly had toggle off)
              if (Array.isArray(existingMessage?.actions)) {
                mappedMessage.actions = existingMessage.actions;
                // Also preserve in parameters.___actions for consistency
                if (!mappedMessage.parameters) {
                  mappedMessage.parameters = {};
                }
                mappedMessage.parameters.___actions = existingMessage.actions;
              }

              // Preserve one-shot "try another model" marker so composer history
              // seeding never treats that turn as the user's new preference.
              if (existingMessage?.parameters?.___oneShotModel) {
                if (!mappedMessage.parameters) {
                  mappedMessage.parameters = {};
                }
                mappedMessage.parameters.___oneShotModel = true;
              }

              // Keep the models the user actually picked. Stream-end can arrive
              // before political routing is persisted on the user turn.
              const existingDisplayIds = existingMessage?.parameters?.___displayModelIds;
              const mappedDisplayIds = mappedMessage.parameters?.___displayModelIds;
              if (
                Array.isArray(existingDisplayIds) && existingDisplayIds.length > 0 &&
                !(Array.isArray(mappedDisplayIds) && mappedDisplayIds.length > 0)
              ) {
                if (!mappedMessage.parameters) {
                  mappedMessage.parameters = {};
                }
                mappedMessage.parameters.___displayModelIds = existingDisplayIds;
              }
            }
            
            // For assistant messages, preserve image-related flags that were set during streaming
            // These flags are needed for proper rendering of image model responses
            const isAssistantMessage = item?.message?.role?.toLowerCase?.() === 'assistant';
            if (isAssistantMessage) {
              const freshChatStore = useChatStore.getState();
              const chat = freshChatStore.chats.find(c => c.id === conversationId);
              const existingMessage = chat?.messages?.find(m => m.id === messageId || m._id === messageId);
              
              // Preserve isRequestingMedia flag (set when message was created for image models)
              if (existingMessage?.isRequestingMedia && !mappedMessage.isRequestingMedia) {
                mappedMessage.isRequestingMedia = existingMessage.isRequestingMedia;
              }
              
              // Preserve isImage flag (set when images arrived via stream)
              if (existingMessage?.isImage && !mappedMessage.isImage) {
                mappedMessage.isImage = existingMessage.isImage;
              }
              
              // Preserve images if they were set locally but backend doesn't have them
              if (existingMessage?.images?.length > 0 && !mappedMessage.images?.length) {
                mappedMessage.images = existingMessage.images;
              }

              // Preserve generated files in races where a stale final/status
              // payload lacks them. Generated images now live in `files`, not
              // the legacy `images` field.
              if (existingMessage?.files?.length > 0 && !mappedMessage.files?.length) {
                mappedMessage.files = existingMessage.files;
              }

              // Same race as addChats: stream_end can serialize before the
              // persisted body is on the message. Empty mapped content would
              // overwrite the tokens already on screen.
              // Exception: the formatter stripped an invented memory-save
              // recap. That cleaned text is a prefix (or empty) and MUST win
              // over the longer streamed claim, or the recap stays on screen.
              if (item.message?.flags?.isMemorySaveClaimStripped === true) {
                mappedMessage.content = item.message.content || '';
              } else {
                mappedMessage.content = pickAuthoritativeMessageText(
                  existingMessage?.content,
                  mappedMessage.content
                );
              }
              mappedMessage.thinkingContent = pickAuthoritativeMessageText(
                existingMessage?.thinkingContent,
                mappedMessage.thinkingContent
              );

              // Political/intent routing persists the inference model on
              // assistant.modelIds. Keep a 1-id user pick (Arya → Gemini) on
              // parameters so the footer card stays Arya. Do not copy a
              // multi-model comparison list onto every response card.
              const incomingDisplay = mappedMessage.parameters?.___displayModelIds;
              if (!(Array.isArray(incomingDisplay) && incomingDisplay.length > 0)) {
                const parent = findParentUserMessage(chat?.messages, existingMessage || mappedMessage);
                const parentDisplay = parent?.parameters?.___displayModelIds
                  || existingMessage?.parameters?.___displayModelIds;
                if (Array.isArray(parentDisplay) && parentDisplay.length === 1) {
                  mappedMessage.parameters = {
                    ...(mappedMessage.parameters || {}),
                    ___displayModelIds: parentDisplay,
                  };
                }
              }
            }
            
            // Debug: Log image status after mapping
            devLog('[Socket] conversation_stream_end - updating message:', {
              messageId,
              role: item?.message?.role,
              originalImageCount: item?.message?.images?.length || 0,
              mappedImageCount: mappedMessage.images?.length || 0,
              hasImages: mappedMessage.images?.length > 0,
              isRequestingMedia: mappedMessage.isRequestingMedia,
              isImage: mappedMessage.isImage,
            });
            
            const result = chatStore.updateLocalChatMessage(conversationId, messageId, mappedMessage);
            if (result) {
              updatedFrontendIds.push(result.id);
              // Debug: Log final message state
              devLog('[Socket] conversation_stream_end - message updated:', {
                messageId: result.id,
                finalImageCount: result.images?.length || 0,
              });
            } else if (
              isUserMessage &&
              (
                mappedMessage.content ||
                (Array.isArray(mappedMessage.files) && mappedMessage.files.length > 0)
              )
            ) {
              const recoveredUserMessage = chatStore.updateLocalChatMessage(conversationId, null, mappedMessage);
              if (recoveredUserMessage) {
                updatedFrontendIds.push(recoveredUserMessage.id);
                return;
              }

              devWarn('[Socket] conversation_stream_end - user message missing locally, adding from final payload:', {
                conversationId,
                messageId,
              });
              chatStore.addMessage(conversationId, {
                ...mappedMessage,
                id: mappedMessage.id || mappedMessage._id || messageId,
                role: 'user',
                isThinking: false,
                isTyping: false,
                isWaiting: false,
                isStreaming: false,
              });
              updatedFrontendIds.push(mappedMessage.id || mappedMessage._id || messageId);
            } else if (isAssistantMessage) {
              const hasRecoverableContent =
                !!mappedMessage.content ||
                !!mappedMessage.thinkingContent ||
                mappedMessage.images?.length > 0 ||
                mappedMessage.files?.length > 0 ||
                mappedMessage.inferences?.length > 0 ||
                mappedMessage.products?.length > 0 ||
                mappedMessage.stockQuotes?.length > 0 ||
                mappedMessage.places?.length > 0 ||
                mappedMessage.isError;

              if (hasRecoverableContent) {
                devWarn('[Socket] conversation_stream_end - assistant message missing locally, adding from final payload:', {
                  conversationId,
                  messageId,
                });
                chatStore.addMessage(conversationId, {
                  ...mappedMessage,
                  id: mappedMessage.id || mappedMessage._id || messageId,
                  role: 'assistant',
                  isThinking: false,
                  isTyping: false,
                  isWaiting: false,
                  isStreaming: false,
                });
                updatedFrontendIds.push(mappedMessage.id || mappedMessage._id || messageId);
              }
            }
          } else {
            // No message id, so it wasn't saved, show an error and stop streaming any assistant messages
            hadMissingMessageId = true;
          }
        });
      }

      const pendingUserMessage = useChatStore.getState().chats
        .find(c => c.id === conversationId)
        ?.messages
        ?.slice()
        .reverse()
        .find(m =>
          m.role === 'user' &&
          m.isPending !== false &&
          (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
        );
      if (pendingUserMessage?.id) {
        chatStore.updateLocalChatMessage(conversationId, pendingUserMessage.id, {
          isPending: false,
        });
      }

      const streamFailedWithoutMessageId = hadMissingMessageId && !streamEndedWithError;
      const effectiveHasError = streamEndedWithError || streamFailedWithoutMessageId;
      const effectiveErrorMessage = errorMessage || (streamFailedWithoutMessageId
        ? 'The response could not be completed. Please try again.'
        : undefined);
      if (streamFailedWithoutMessageId && effectiveErrorMessage) {
        try {
          const { addToast } = useToastStore.getState().actions;
          addToast(effectiveErrorMessage, 'error');
        } catch (_toastErr) {
          // Best-effort UX signal only
        }
      }
      
      // ALWAYS clean up any remaining streaming assistant messages for this conversation
      // This ensures no messages get "stuck" in streaming state even with ID mismatches or network issues
      // IMPORTANT: Get fresh state here since chatStore may be stale after the updates above
      const freshChatStore = useChatStore.getState();
      const chat = freshChatStore.chats.find(c => c.id === conversationId);
      const streamingMessages = chat?.messages?.filter(m => 
        m.role === 'assistant' && 
        (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking) &&
        !foundMessageIds.includes(m.id) &&
        !updatedFrontendIds.includes(m.id)
      ) || [];

      devLog('[Socket] conversation_stream_end cleanup:', {
        foundMessageIds,
        updatedFrontendIds,
        streamingMessagesCount: streamingMessages.length,
        streamingMessageIds: streamingMessages.map(m => m.id),
      });

      // If there are streaming messages, update them
      if (streamingMessages.length > 0) {
        streamingMessages.forEach(streamingMessage => {
          const { shouldDisplayMessage } = getMessageDisplayInfo(streamingMessage, false);

          // Safety check: NEVER remove messages that have actual content, images, files, or thinking content
          // This prevents a bug where messages with content disappear after the icon bar loads
          const hasActualContent = streamingMessage.content && streamingMessage.content.length > 0;
          const hasActualImages = Array.isArray(streamingMessage.images) && streamingMessage.images.length > 0;
          const hasActualFiles = Array.isArray(streamingMessage.files) && streamingMessage.files.length > 0;
          const hasThinkingContent = streamingMessage.thinkingContent && streamingMessage.thinkingContent.length > 0;
          const hasAnyContent = hasActualContent || hasActualImages || hasActualFiles || hasThinkingContent;

          if (!effectiveHasError && !effectiveErrorMessage && !shouldDisplayMessage && !hasAnyContent) {
            devLog('[Socket] conversation_stream_end cleanup: removing empty message:', {
              messageId: streamingMessage.id,
            });
            freshChatStore.removeMessage(conversationId, streamingMessage.id);
          } else {
            // Clear streaming flags but preserve content
            if (hasAnyContent && !shouldDisplayMessage) {
              devLog('[Socket] conversation_stream_end cleanup: preserving message with content (shouldDisplayMessage was false):', {
                messageId: streamingMessage.id,
                hasActualContent,
                hasActualImages,
                hasActualFiles,
                hasThinkingContent,
              });
            }
            freshChatStore.updateLocalChatMessage(conversationId, streamingMessage.id, {
              content: effectiveErrorMessage && !hasAnyContent ? effectiveErrorMessage : streamingMessage.content,
              isThinking: false,
              isTyping: false,
              isWaiting: false,
              isStreaming: false,
              isError: hasAnyContent ? (streamingMessage.isError || false) : (effectiveHasError || false),
              errorMessage: effectiveHasError ? effectiveErrorMessage : undefined,
              // Preserve existing images and files - they may have been set by assistant_message_image_stream
              // Setting to undefined would overwrite them and cause image generation to disappear
              buttons: actions || undefined,
              inferenceStatus: undefined, // Clear inference status when stream ends
              inferenceStage: undefined,
              inferenceStatusTimestamp: undefined
            });
          }
        });
      }

      const completedWithAssistantReply =
        !effectiveHasError &&
        data?.some(item =>
          item?.message?.role?.toLowerCase?.() === 'assistant' &&
          (
            item.message.content ||
            item.message.images?.length > 0 ||
            item.message.files?.length > 0
          )
        );
      if (completedWithAssistantReply) {
        notifyPetReplyCompleted(conversationId);

        let generatedPdf = null;
        let generatedPresentation = null;
        let generatedSpreadsheet = null;
        let generatedDocument = null;
        for (const item of data || []) {
          if (item?.message?.role?.toLowerCase?.() !== 'assistant') continue;
          const messageFiles = item.message.files || [];
          generatedDocument = messageFiles.find(file => {
            const mime = (file?.mimeType || file?.type || '').toLowerCase();
            const extension = (file?.extension || '').toLowerCase();
            return mime === 'application/vnd.gab-ai.document+json'
              || extension === '.gabdoc'
              || extension === 'gabdoc'
              || file?.parameters?.documentKind === 'editable-document';
          });
          generatedPdf = messageFiles.find(file => {
            const mime = (file?.mimeType || file?.type || '').toLowerCase();
            const extension = (file?.extension || '').toLowerCase();
            const name = (file?.name || file?.originalName || '').toLowerCase();
            return mime === 'application/pdf'
              || extension === '.pdf'
              || extension === 'pdf'
              || name.endsWith('.pdf');
          });
          generatedPresentation = messageFiles.find(file => {
            const mime = (file?.mimeType || file?.type || '').toLowerCase();
            const extension = (file?.extension || '').toLowerCase();
            const name = (file?.name || file?.originalName || '').toLowerCase();
            return file?.parameters?.presentationSpec
              && (mime === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                || extension === '.pptx'
                || extension === 'pptx'
                || name.endsWith('.pptx'));
          });
          generatedSpreadsheet = messageFiles.find(file => {
            const mime = (file?.mimeType || file?.type || '').toLowerCase();
            const extension = (file?.extension || '').toLowerCase();
            const name = (file?.name || file?.originalName || '').toLowerCase();
            return file?.parameters?.spreadsheetSpec
              && (mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                || extension === '.xlsx'
                || extension === 'xlsx'
                || name.endsWith('.xlsx'));
          });
          if (generatedDocument || generatedPdf || generatedPresentation || generatedSpreadsheet) break;
        }

        // Deferred require avoids a socket.js <-> files.js module cycle.
        const {
          canUseDesktopEditors,
          openArtifactViewerForFile,
        } = require('@/constants/utils/files');
        const useDesktopEditors = Platform.OS === 'web' && canUseDesktopEditors();

        // Only auto-open for the conversation the user is actually looking
        // at. A reply completing in a background chat would otherwise yank an
        // editor/viewer open over the current one.
        const viewedFromPath = typeof window === 'undefined'
          ? null
          : window.location.pathname.match(/\/c\/([^/]+)/)?.[1];
        // Native (and some web shells) track the open chat in the route store.
        const viewedFromRoute = useRouteStore.getState().currentLibraryId || null;
        const viewedConversationId = viewedFromPath || viewedFromRoute;
        // A missing id means a brand-new chat that hasn't been routed yet,
        // which is the common "create me a document" path — allow that.
        if (viewedConversationId && conversationId &&
            String(viewedConversationId) !== String(conversationId)) {
          generatedDocument = null;
          generatedPresentation = null;
          generatedSpreadsheet = null;
          generatedPdf = null;
        }

        if (useDesktopEditors) {
          // Widths are resolved per sidebar type inside openContextSidebar, so
          // re-opening the same file (in-place AI revision) keeps whatever
          // width the user resized that panel to.
          if (generatedDocument) {
            const { useUIStore } = require('@/store/ui');
            useUIStore.getState().openContextSidebar('document-editor', { file: generatedDocument });
          } else if (generatedPresentation) {
            const { useUIStore } = require('@/store/ui');
            useUIStore.getState().openContextSidebar('presentation-editor', { file: generatedPresentation });
          } else if (generatedSpreadsheet) {
            const { useUIStore } = require('@/store/ui');
            useUIStore.getState().openContextSidebar('spreadsheet-editor', { file: generatedSpreadsheet });
          } else if (generatedPdf) {
            const { useUIStore } = require('@/store/ui');
            useUIStore.getState().openContextSidebar('pdf-preview', { file: generatedPdf });
          }
        } else {
          // Phones / tablets / native: no editor sidebar. Open the read-only
          // artifact viewer so follow-up revisions aren't just a text claim
          // that "the document is below" with nothing obvious to tap.
          const mobileArtifact = generatedDocument || generatedPresentation || generatedSpreadsheet;
          if (mobileArtifact) {
            openArtifactViewerForFile(mobileArtifact);
          }
        }
      }

      // After a request completes, refresh user credits so UI reflects the new balance
      try {
        const authStore = useAuthStore.getState();
        if (authStore?.fetchUserCredits) {
          authStore.fetchUserCredits();
        }
      } catch (e) {
        // Silently fail - credit refresh is best effort
      }

      // After successful inference, check if we should show card expiration warning
      // Only show if: inference was successful (no error), warning data exists, and not shown today
      if (!effectiveHasError && !effectiveErrorMessage) {
        try {
          const authStore = useAuthStore.getState();
          if (authStore.shouldShowCardExpirationWarning()) {
            // Set flag to trigger warning display in the UI
            // We use a slight delay to let the response render first
            setTimeout(() => {
              const currentAuthStore = useAuthStore.getState();
              if (currentAuthStore.cardExpirationWarning) {
                // Mark as shown so it doesn't show again today
                currentAuthStore.markCardExpirationWarningShown();
                // Update the warning object to indicate it should be displayed
                currentAuthStore.setCardExpirationWarning({
                  ...currentAuthStore.cardExpirationWarning,
                  shouldDisplay: true,
                  displayTriggeredAt: Date.now(),
                  conversationId: conversationId,
                });
              }
            }, 1500); // 1.5 second delay to let the response settle
          }
        } catch (e) {
          // Silently fail - card expiration warning is best effort
        }
      }

      // If a token refresh was deferred during streaming, reconnect now.
      // Use the token/userId captured at schedule time (they're guaranteed to
      // be set); reading from the auth store here can race with another
      // refresh that fires between schedule and stream_end.
      if (this._pendingReconnect) {
        const token = this._deferredReconnectToken;
        const userId = this._deferredReconnectUserId;
        this._clearDeferredReconnect();
        if (token && userId) {
          devLog('[Socket] Executing deferred reconnect after stream completed');
          // Small delay to let stream_end processing finish cleanly
          setTimeout(() => {
            this.connect(token, userId);
          }, 500);
        }
      }

    });

    // Handle connection
    this.socket.on('connect', () => {
      this._authRecovery?.cancel();
      this.isConnected = true;

      // Clear any 429 backoff now that we're connected: restore the original
      // reconnectionDelay so future reconnects use the normal backoff curve.
      if (this._rateLimitedUntilMs) {
        this._rateLimitedUntilMs = 0;
      }
      if (this._origReconnectionDelay && this.socket?.io?.opts) {
        this.socket.io.opts.reconnectionDelay = this._origReconnectionDelay;
        this._origReconnectionDelay = null;
      }

      // Dismiss stale connection-error toasts so a brief blip doesn't leave
      // "check your internet connection" lingering after we're back online.
      try {
        useToastStore.getState().actions.removeToastsByCategory('connection');
      } catch (_) {}

      // Reconcile the (persisted) conversation list against the server on
      // every connect: the initial connect catches deletions made on other
      // devices while the app was closed, and reconnects catch
      // conversation_deleted events missed during the disconnect.
      this._softSyncChatList();

      // Send anything queued while offline. Runs after the soft-sync so the
      // chat list is fresh before bubbles flip from queued to in-flight.
      this._flushQueuedSends();

      // Kick any new-chat inferences that couldn't fire while disconnected.
      this._flushPendingKicks();

      // On reconnect, clean up any stale streaming messages that might have been left
      // in a stuck state due to socket disconnect before stream_end was received
      try {
        const chatStore = useChatStore.getState();
        const inferencingChatIds = [];

        chatStore.chats.forEach(chat => {
          const hasStaleStreaming = chat?.messages?.some(m => 
            m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
          );
          const isInferencing = chat?.isInferencing;
          
          if (hasStaleStreaming && !isInferencing) {
            const staleMessages = chat.messages.filter(m => 
              m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
            );
            staleMessages.forEach(msg => {
              chatStore.updateLocalChatMessage(chat.id, msg.id, {
                isThinking: false,
                isTyping: false,
                isWaiting: false,
                isStreaming: false,
                inferenceStatus: undefined,
                inferenceStage: undefined,
                inferenceStatusTimestamp: undefined
              });
            });
          }

          // conversation_stream_end may have been lost while the socket was down
          if (isInferencing) {
            inferencingChatIds.push(chat.id);
          }
        });

        // Give the server a moment to deliver any pending events after
        // subscribe completes, then recover anything still stuck
        if (inferencingChatIds.length > 0) {
          setTimeout(() => {
            const freshStore = useChatStore.getState();
            inferencingChatIds.forEach(chatId => {
              const freshChat = freshStore.chats.find(c => c.id === chatId);
              if (freshChat?.isInferencing) {
                devLog('[Socket] Reconnect: triggering recovery for inferencing chat', { chatId });
                this._recoverStalledConversation(chatId, 'reconnect');
              }
            });
          }, 3000);
        }
      } catch (e) {
        // Silently fail - cleanup is best effort
      }

      // Subscribe to user-specific channel
      if (this.userId) {
        this.socket.emit("subscribe", { userId: this.userId });
      }
    });

    // Handle reload request
    this.socket.on('reload', () => {
      if (Platform.OS === 'web') {
        window.location.href = '/';
      }
    });

    // Socket-level connection events
    this.socket.on('upgrade', () => {
      // Transport upgraded
    });

    this.socket.on('upgradeError', (err) => {
      console.error('Socket upgrade error:', err);
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      // Flush buffered tokens to the store before clearing so the user
      // doesn't lose the last ~50ms of streamed content on disconnect.
      try { this._flushTokenStreamBuffer(); } catch (_) {}
      if (this.tokenStreamInterval) {
        clearInterval(this.tokenStreamInterval);
        this.tokenStreamInterval = null;
      }
      this.tokenStreamBuffer.clear();
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    // Set up ping-pong for connection testing
    this.socket.on('pong', () => {
      // Connection verified
    });
  }

  // Set up Socket.IO engine-level events
  _setupEngineEventHandlers() {
    if (!this.socket?.io) return;

    // Connection state handling
    this.socket.io.on("reconnect_attempt", () => {
      // Reconnection in progress
    });

    this.socket.io.on("reconnect_error", (error) => {
      // A failed attempt is expected while socket.io is backing off (and is
      // especially common during native Fast Refresh). It is not terminal;
      // only `reconnect_failed` below is. Avoid surfacing every retry as a
      // red in-app development error while the connection is self-healing.
      devWarn('Socket reconnection attempt failed:', error);
    });

    this.socket.io.on("reconnect_failed", () => {
      console.error('Socket reconnection failed');
    });

    // Transport handling
    this.socket.io.on("transport", (transport) => {
      transport.on("error", (err) => {
        console.error('Socket transport error:', err);
      });
    });
  }

  // Add method to verify connection and token
  _verifyConnection(currentToken, userId) {
    // If we have no socket or no current token, disconnect
    if (!currentToken) {
      this.disconnectSocket();
      return false;
    }
    // If we have no user id, disconnect
    if (!userId) {
      this.disconnectSocket();
      return false;
    }

    // If the socket was intentionally paused because the tab went hidden,
    // refuse to auto-reconnect from background code — the visibilitychange
    // handler resumes it when the user actually returns. Without this guard
    // any incidental `_emitSocketEvent` would silently undo the pause and
    // defeat the per-tab load reduction.
    if (
      this._pausedDueToHidden &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden'
    ) {
      return false;
    }

    // If socket exists but token changed, reconnect
    if (this.socket && this.lastToken !== currentToken) {
      this.connect(currentToken, userId);
      return true;
    }

    // If no socket but we have token, connect
    if (!this.socket && currentToken) {
      this.connect(currentToken, userId);
      return true;
    }

    // If socket exists but user id changed, disconnect
    if (this.socket && this.userId !== userId) {
      this.disconnectSocket();
      return false;
    }

    // If socket exists and token matches, check connection
    if (this.socket && this.lastToken === currentToken) {
      if (!this.isConnected) {
        this.connect(currentToken, userId);
        return true;
      }
      return true;
    }

    return false;
  }

  // Emit an event. Returns true when the emit actually went out, false when
  // the connection guard bailed (token changed, socket null, reconnect
  // forced). Callers that have optimistic state riding on the emit must
  // roll back on a `false` return — otherwise the UI gets stranded in a
  // "sent but server never heard it" state.
  _emitSocketEvent(userId, event, data) {
    // Get current token from auth store
    const authStore = useAuthStore.getState();
    const currentToken = authStore.token;

    // Verify connection with current token
    if (!this._verifyConnection(currentToken, userId)) {
      devWarn('Socket not connected or invalid token. Cannot emit event:', event);
      return false;
    }

    // At this point we should have a valid connection
    this.socket.emit(event, data);
    return true;
  }

  // Build the send_prompt payload shared by live sends and queued flushes.
  // Resolves fast mode (message-level override → chat flag), flushes pending
  // editor autosaves so an AI revision sees the latest draft, and attaches
  // the active-editor context for artifact revisions.
  _buildSendPromptPayload({ userId, conversationId, formattedMessage, optimisticMessageId }) {
    const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
    const clientInfo = getClientInfo();

    // Flush any pending editor autosaves (document/presentation drafts) so
    // an AI revision triggered by this message sees the user's latest edits
    // instead of a draft still sitting in a debounce window. Fire-and-forget:
    // the PUT reaches the backend well before intent classification finishes.
    try {
      const { flushEditorSaves } = require('@/lib/editorSaveRegistry');
      flushEditorSaves();
    } catch (_) { /* never block sending */ }

    // Fast mode can come from:
    // 1. formattedMessage.fastMode (for new conversations, set via selectedParameters)
    // 2. chat.flags.fastMode (for existing conversations, set via toggleFastMode)
    let fastMode = formattedMessage.fastMode || false;
    if (!fastMode && chat?.flags?.fastMode) {
      fastMode = true;
    }

    // Tell the backend which editable artifact (document / spreadsheet /
    // presentation) is open in the contextual sidebar, so edit requests in
    // the chat are applied to that exact file — with any selected model.
    let activeEditor = null;
    try {
      const { useUIStore } = require('@/store/ui');
      const ui = useUIStore.getState();
      const editorKinds = {
        'document-editor': 'document',
        'spreadsheet-editor': 'spreadsheet',
        'presentation-editor': 'presentation',
      };
      const kind = ui.isContextSidebarVisible ? editorKinds[ui.contextSidebarType] : null;
      const sidebarFile = ui.contextSidebarProps?.file;
      const fileId = sidebarFile?.id || sidebarFile?._id;
      // Only claim an active editor when the device can actually show one —
      // stale persisted sidebar state on a phone must not hijack chat
      // messages into the artifact revision pipeline.
      const { canUseDesktopEditors } = require('@/constants/utils/files');
      if (kind && fileId && canUseDesktopEditors()) {
        activeEditor = { kind, fileId: String(fileId) };
      }
    } catch (_) { /* never block sending */ }

    return {
      userId: userId,
      conversationId: conversationId,
      ...formattedMessage,
      clientMessageId: optimisticMessageId,
      fastMode: fastMode, // Explicitly set fastMode (overrides any value from formattedMessage)
      clientInfo,
      // Device IANA timezone so the model gets the user's local clock, not the server's.
      timezone: getUserTimezone() || undefined,
      activeEditor,
    };
  }

  // Queue a message while offline. The optimistic bubble keeps the user's
  // text visible (flagged isQueued so the UI shows a waiting-for-connection
  // state) and the entry flushes through the normal send path on the next
  // connect. One queued message per conversation — the queued bubble counts
  // as pending, mirroring the live path's one-pending-message rule.
  _queueOfflineSend({ userId, conversationId, formattedMessage }) {
    if (!conversationId || !formattedMessage) {
      devWarn('Conversation ID and message are required');
      return false;
    }

    const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
    if (!chat) {
      devWarn('[Socket] _queueOfflineSend: conversation not found', { conversationId });
      return false;
    }

    const hasPendingMessage = (chat.messages || []).some(m =>
      m.role === 'user' &&
      m.isPending !== false &&
      (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
    );
    if (hasPendingMessage || chat.isInferencing) {
      const { addToast } = useToastStore.getState().actions;
      addToast(getTranslation('socket.messageProcessing'), 'error');
      return false;
    }

    if (formattedMessage?.fileIds?.some(id => id.includes('uploading'))) {
      devWarn('Files are still uploading. Please wait...');
      const { addToast } = useToastStore.getState().actions;
      addToast(getTranslation('socket.waitForUploads'), 'error');
      return false;
    }

    const optimisticMessageId = 'msg_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    const files = formattedMessage.fileIds.flatMap(id => {
      const file = useFileStore.getState().files[id];
      return file ? [file] : [];
    });
    const composerState = useChatInputStore.getState().inputStates?.[conversationId];
    const turnParameters = parametersForOptimisticUserMessage(formattedMessage, composerState);
    const attributedFormattedMessage = turnParameters
      ? { ...formattedMessage, parameters: turnParameters }
      : formattedMessage;

    useChatStore.getState().addMessage(conversationId, {
      id: optimisticMessageId,
      content: formattedMessage.text,
      modelIds: formattedMessage.modelIds,
      agentIds: formattedMessage.agentIds,
      quoteContent: formattedMessage.quote,
      actions: formattedMessage.actions,
      files: files,
      parameters: turnParameters,
      role: 'user',
      timestamp: new Date().toISOString(),
      isPending: true,
      isQueued: true,
    });

    this.pendingSendQueue.set(conversationId, {
      userId,
      conversationId,
      formattedMessage: attributedFormattedMessage,
      optimisticMessageId,
      queuedAt: Date.now(),
    });

    const { addToast } = useToastStore.getState().actions;
    addToast(
      getTranslation('socket.messageQueuedOffline') || "You're offline — the message will send when you're back online.",
      'info',
      [],
      { category: 'connection' },
    );
    return true;
  }

  // Flush queued offline sends through the normal send path. Called on every
  // socket connect (initial + reconnect). The queued bubble is reused — the
  // payload carries its id as clientMessageId, so the server's
  // new_user_message ack swaps it to a real id just like a live send.
  _flushQueuedSends() {
    if (this.pendingSendQueue.size === 0) return;
    this.pendingSendQueue.forEach((queued, conversationId) => {
      try {
        this._flushQueuedSend(conversationId, queued);
      } catch (e) {
        devWarn('[Socket] _flushQueuedSends: flush failed', { conversationId, error: e?.message || e });
      }
    });
  }

  _flushQueuedSend(conversationId, queued) {
    const chatStore = useChatStore.getState();
    const chat = chatStore.chats.find(c => c.id === conversationId);
    const bubble = chat?.messages?.find(m => m.id === queued.optimisticMessageId);

    // Chat or bubble vanished while offline (deleted, or a merge dropped it)
    // — drop the queue entry rather than resurrecting a ghost message.
    if (!chat || !bubble) {
      this.pendingSendQueue.delete(conversationId);
      return;
    }

    this.pendingSendQueue.delete(conversationId);

    // Clear any previous rejection flag so new inference events are processed
    this.rejectedConversations.delete(conversationId);

    chatStore.updateLocalChatMessage(conversationId, queued.optimisticMessageId, {
      isQueued: false,
    });
    chatStore.updateLocalChatDetails(conversationId, {
      isInferencing: true,
      isInferencingWaiting: true,
    });
    this._markStreamActivity(conversationId);

    const payload = this._buildSendPromptPayload({
      userId: queued.userId,
      conversationId,
      formattedMessage: queued.formattedMessage,
      optimisticMessageId: queued.optimisticMessageId,
    });

    let emitted = false;
    try {
      emitted = this._emitSocketEvent(queued.userId, 'send_prompt', payload);
    } catch (emitError) {
      devWarn('[Socket] _flushQueuedSend: emit threw', emitError?.message || emitError);
      emitted = false;
    }

    if (!emitted) {
      // Back into the queue — a later connect retries. The bubble keeps the
      // user's text; flags roll back so the chat doesn't look stuck loading.
      devWarn('[Socket] _flushQueuedSend: emit failed — re-queuing', { conversationId });
      try {
        chatStore.updateLocalChatMessage(conversationId, queued.optimisticMessageId, {
          isQueued: true,
        });
        chatStore.updateLocalChatDetails(conversationId, {
          isInferencing: false,
          isInferencingWaiting: false,
        });
      } catch (e) { /* best effort */ }
      this.pendingSendQueue.set(conversationId, queued);
      return;
    }

    try {
      const user = useAuthStore.getState().user;
      if (user?.stats) trackWhopFirstPrompt(user.stats.total_sent_message_all_count ?? 0);
    } catch (_) { /* never block sending */ }
  }

  // Send a prompt to the server
  emitSendUserMessage({ userId, conversationId, formattedMessage }) {
    if (!this.isConnected) {
      // Offline: queue the message instead of dead-ending with a toast. The
      // optimistic bubble holds the user's text and the queue flushes through
      // the normal send path on the next socket connect.
      return this._queueOfflineSend({ userId, conversationId, formattedMessage });
    }
    if (!conversationId || !formattedMessage) {
      devWarn('Conversation ID and message are required');
      return false;
    }

    // Check if there's already a pending message
    const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
    const hasPendingMessage = chat?.messages?.some(m =>
      m.role === 'user' &&
      m.isPending !== false &&
      (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
    );
    const files = formattedMessage.fileIds.flatMap(id => {
      const file = useFileStore.getState().files[id];
      return file ? [file] : [];
    });
    const isInferencing = chat?.isInferencing;

    // Check if any files are still uploading
    if (formattedMessage?.fileIds?.some(id => id.includes('uploading'))) {
      devWarn('Files are still uploading. Please wait...');
      const { addToast } = useToastStore.getState().actions;
      addToast(getTranslation('socket.waitForUploads'), 'error');
      return false;
    }

    if (hasPendingMessage || isInferencing) {
      devWarn('Message already processing. Please wait...');
      const { addToast } = useToastStore.getState().actions;
      addToast(getTranslation('socket.messageProcessing'), 'error');
      // Auto-trigger recovery. A stuck `isInferencing` with no actively
      // streaming assistant message is almost always the result of a missed
      // `conversation_stream_end` (socket blip, backgrounding, etc.). The
      // recovery path hits the status endpoint and will clear the local
      // flag once the backend confirms it's done — so the user's next send
      // attempt actually goes through instead of being silently swallowed.
      // Fire-and-forget; the watchdog inside handles its own retries.
      try {
        const hasActiveStream = (chat?.messages || []).some(m =>
          m.role === 'assistant' &&
          (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
        );
        if (!hasActiveStream) {
          devLog('[Socket] emitSendUserMessage: stuck inferencing with no active stream — triggering recovery', { conversationId });
          this._recoverStalledConversation(conversationId, 'stuck_send_guard');
        }
      } catch (e) {
        devWarn('[Socket] emitSendUserMessage: recovery trigger failed', e?.message || e);
      }
      return false;
    }

    // Add message to store immediately
    // Clear any previous rejection flag so new inference events are processed
    this.rejectedConversations.delete(conversationId);

    const optimisticMessageId = 'msg_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    const composerState = useChatInputStore.getState().inputStates?.[conversationId];
    const turnParameters = parametersForOptimisticUserMessage(formattedMessage, composerState);
    const attributedFormattedMessage = turnParameters
      ? { ...formattedMessage, parameters: turnParameters }
      : formattedMessage;

    useChatStore.getState().addMessage(conversationId, {
      id: optimisticMessageId,
      content: formattedMessage.text,
      modelIds: formattedMessage.modelIds,
      agentIds: formattedMessage.agentIds,
      quoteContent: formattedMessage.quote,
      actions: formattedMessage.actions,
      files: files,
      // Keep client-only flags like ___oneShotModel so history seeding doesn't
      // treat this turn as the user's new composer preference. Also stamp
      // ___isAutoMode / ___displayModelIds locally so the footer hides Auto
      // routing and keeps a manual Arya card even when political intent
      // later swaps the inference model.
      parameters: turnParameters,
      role: 'user',
      timestamp: new Date().toISOString(),
      isPending: true,
    });

    // Immediately set the chat to not inferencing
    useChatStore.getState().updateLocalChatDetails(conversationId, {
      isInferencing: true,
      isInferencingWaiting: true,
    });

    this._markStreamActivity(conversationId);

    const payload = this._buildSendPromptPayload({
      userId,
      conversationId,
      formattedMessage: attributedFormattedMessage,
      optimisticMessageId,
    });

    // Send to server. _emitSocketEvent can silently bail (token changed,
    // socket null, verifyConnection forced a reconnect) — in any of those
    // cases the optimistic message + `isInferencing=true` we set above would
    // be stranded, leaving the chat stuck loading with a phantom pending
    // bubble. Roll back on a failed emit so the user can retry and the
    // store stays consistent.
    trackActivationOnce(userId, ACTIVATION_ACTION.FIRST_MESSAGE_SENT);

    let emitted = false;
    try {
      emitted = this._emitSocketEvent(userId, 'send_prompt', payload);
    } catch (emitError) {
      devWarn('[Socket] emitSendUserMessage: emit threw', emitError?.message || emitError);
      emitted = false;
    }

    if (!emitted) {
      devWarn('[Socket] emitSendUserMessage: emit failed — rolling back optimistic state', { conversationId });
      try {
        useChatStore.getState().removeMessage(conversationId, optimisticMessageId);
      } catch (e) { /* best effort */ }
      useChatStore.getState().updateLocalChatDetails(conversationId, {
        isInferencing: false,
        isInferencingWaiting: false,
      });
      const { addToast } = useToastStore.getState().actions;
      addToast(
        getTranslation('socket.fastModeNotConnected') || 'Connection lost. Please try again.',
        'error',
        [],
        { category: 'connection' },
      );
      return false;
    }

    try {
      const user = useAuthStore.getState().user;
      if (user?.stats) trackWhopFirstPrompt(user.stats.total_sent_message_all_count ?? 0);
    } catch (_) { /* never block sending */ }

    return true;
  }

  // Regenerate last message.
  // Backend contract: event must be "regenerate_message" (snake_case). Payload: conversationId, userId, messageId (assistant message id); optional: regenerateOptions, clientInfo.
  emitRegenerateMessage({ userId, conversationId, messageId, regenerateType }) {
    if (!this.isConnected) {
      devWarn('Socket not connected. Cannot regenerate message');
      return;
    }
    if (!userId || !conversationId || !messageId) {
      devWarn('User ID, conversation ID, and message ID are required for regenerate_message');
      return;
    }

    // Check if there's already a pending user message that hasn't been assigned
    // a backend ID yet — that means another send is truly in progress.
    const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
    const hasPendingMessage = chat?.messages?.some(m =>
      m.role === 'user' &&
      m.isPending !== false &&
      (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
    );

    // Intentionally NOT blocking on `chat.isInferencing` here. Regenerating the
    // most recent completed message is always a valid "replace" action — if a
    // new user message is actively in flight, `hasPendingMessage` catches it;
    // otherwise a stale `isInferencing` flag (left over from a race on image
    // generation, etc.) shouldn't prevent the user from retrying. If the
    // backend's own DB-level flag is genuinely still set, it will reject and
    // surface an error toast to the user.
    if (hasPendingMessage) {
      devWarn('Message already processing. Please wait...');
      const { addToast } = useToastStore.getState().actions;
      addToast(getTranslation('socket.messageProcessing'), 'error');
      return;
    }

    // Clear any previous rejection flag so new inference events are processed
    this.rejectedConversations.delete(conversationId);

    // Just hide it, cause it theres an error we want to show it back again
    // If theres not an error, we just replace it with the new one
    useChatStore.getState().hideMessage(conversationId, messageId);

    // Immediately set the chat to not inferencing
    useChatStore.getState().updateLocalChatDetails(conversationId, {
      isInferencing: true,
      isInferencingWaiting: true,
    });

    this._markStreamActivity(conversationId);

    // Check if fast mode is enabled for this conversation
    const fastMode = chat?.flags?.fastMode || false;

    // Retry honors the user's current model selection: when model(s) are
    // manually selected in the composer for this conversation, send them so
    // the backend regenerates with those instead of the models stored on the
    // original message. Empty selection (Auto) keeps the original models.
    let selectedModelIds = [];
    try {
      const inputStore = useChatInputStore.getState();
      const inputState = inputStore.inputStates?.[conversationId];
      selectedModelIds = inputStore.filterValidIds(inputState?.selectedModelIds);
    } catch (e) {
      devWarn('Could not read selected models for regenerate:', e);
    }

    this._emitSocketEvent(userId, 'regenerate_message', {
      userId: userId,
      conversationId: conversationId,
      messageId: messageId,
      fastMode: fastMode,
      clientInfo: getClientInfo(),
      timezone: getUserTimezone() || undefined,
      regenerateOptions: {
        type: regenerateType,
        ...(selectedModelIds.length > 0 ? { modelIds: selectedModelIds } : {}),
      },
    });
  }

  // Trigger inference for a conversation (used for new conversations after creation)
  emitKickConversation(userId, conversationId, choice = {}) {
    if (!conversationId || !userId) {
      devWarn('Conversation ID and user ID are required');
      return;
    }

    // Clear any previous rejection flag so new inference events are processed
    this.rejectedConversations.delete(conversationId);

    // Set convo to inferencing state
    useChatStore.getState().updateLocalChatDetails(conversationId, {
      isInferencing: true,
      isInferencingWaiting: true,
    });

    this._markStreamActivity(conversationId);

    if (!this.isConnected) {
      // Hold the kick until the next connect instead of leaving the new chat
      // sitting idle — the connect handler flushes pending kicks.
      devWarn('[Socket] emitKickConversation: offline — holding kick until reconnect', { conversationId });
      this.pendingKicks.set(conversationId, { userId, attempts: 0, choice });
      return;
    }

    this._sendKick(userId, conversationId, 0, choice);
  }

  _sendKick(userId, conversationId, attempt, choice = {}) {
    const chatStore = useChatStore.getState();
    const chat = chatStore.chats.find(c => c.id === conversationId);
    // Chat vanished before the kick went out (deleted, temp cleanup) — stop.
    if (!chat) {
      this.pendingKicks.delete(conversationId);
      return;
    }
    // Read fast mode fresh on every attempt — the flag can be set during
    // conversation creation and arrive between retries.
    const fastMode = chat?.flags?.fastMode || false;

    let emitted = false;
    try {
      emitted = this._emitSocketEvent(userId, 'trigger_conversation_socket_inference', {
        userId: userId,
        conversationId: conversationId,
        fastMode: fastMode,
        workModeChoice: choice.workModeChoice,
        workModeMessageId: choice.workModeMessageId,
        clientInfo: getClientInfo(),
        timezone: getUserTimezone() || undefined,
      });
    } catch (e) {
      emitted = false;
    }

    if (emitted) {
      this.pendingKicks.delete(conversationId);
      const timer = this.kickRetryTimers.get(conversationId);
      if (timer) {
        clearTimeout(timer);
        this.kickRetryTimers.delete(conversationId);
      }
      return;
    }

    // The emit never left the client (token refresh, socket mid-reconnect).
    // Only such emits are retried — nothing reached the server, so there's
    // no double-inference risk.
    const nextAttempt = attempt + 1;
    if (nextAttempt > 3) {
      this.pendingKicks.delete(conversationId);
      useChatStore.getState().updateLocalChatDetails(conversationId, {
        isInferencing: false,
        isInferencingWaiting: false,
      });
      const { addToast } = useToastStore.getState().actions;
      addToast(
        getTranslation('socket.fastModeNotConnected') || 'Connection lost. Please try again.',
        'error',
        [],
        { category: 'connection' },
      );
      return;
    }

    this.pendingKicks.set(conversationId, { userId, attempts: nextAttempt, choice });
    const delay = [2000, 5000, 10000][nextAttempt - 1] || 10000;
    const timer = setTimeout(() => {
      this.kickRetryTimers.delete(conversationId);
      // If we're offline again, the connect handler flushes pending kicks.
      if (!this.isConnected) return;
      this._sendKick(userId, conversationId, nextAttempt, choice);
    }, delay);
    this.kickRetryTimers.set(conversationId, timer);
  }

  _flushPendingKicks() {
    if (this.pendingKicks.size === 0) return;
    this.pendingKicks.forEach((kick, conversationId) => {
      try {
        this._sendKick(kick.userId, conversationId, kick.attempts || 0, kick.choice);
      } catch (e) {
        devWarn('[Socket] _flushPendingKicks failed', { conversationId, error: e?.message || e });
      }
    });
  }

  // Retrieve conversation title
  emitRetrieveConversationTitle(userId, conversationId) {
    if (!this.isConnected) {
      devWarn('Socket not connected. Cannot retrieve title');
      return;
    }
    if (!conversationId || !userId) {
      devWarn('Conversation ID and user ID are required');
      return;
    }

    this._emitSocketEvent(userId, 'retrieve_conversation_title', {
      userId: userId,
      conversationId: conversationId,
    });
  }

  emitCancelUserConversationInference(userId, conversationId) {
    if (!conversationId || !userId) {
      devWarn('Conversation ID and user ID are required');
      return;
    }
    
    // Immediately stop streaming on client side as fallback
    // This handles cases where socket might disconnect before success event arrives
    const chatStore = useChatStore.getState();
    
    chatStore.updateLocalChatDetails(conversationId, {
      isInferencing: false,
      isInferencingWaiting: false,
    });
    
    // Stop any streaming messages immediately
    const chat = chatStore.chats.find(c => c.id === conversationId);
    const streamingMessages = chat?.messages?.filter(m => 
      m.role === 'assistant' && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
    ) || [];
    
    if (streamingMessages.length > 0) {
      streamingMessages.forEach(streamingMessage => {
        chatStore.updateLocalChatMessage(conversationId, streamingMessage.id, {
          isThinking: false,
          isTyping: false,
          isWaiting: false,
          isStreaming: false,
          isTruncated: true,
          stopReason: 'cancelled',
          inferenceStatus: undefined,
          inferenceStage: undefined,
          inferenceStatusTimestamp: undefined
        });

        // Persist partial content to backend before cancelling so it survives page reload
        const partialContent = streamingMessage.content;
        const partialThinking = streamingMessage.thinkingContent;
        if (this.isConnected && (partialContent || partialThinking)) {
          this._emitSocketEvent(userId, 'save_partial_response', {
            conversationId,
            messageId: streamingMessage.id,
            content: partialContent || '',
            thinkingContent: partialThinking || '',
          });
        }
      });
    }
    
    // Emit cancel AFTER save_partial_response so the backend can persist first
    if (this.isConnected) {
      this._emitSocketEvent(userId, 'cancel_user_conversation_inference', {
        userId: userId,
        conversationId: conversationId,
      });
    }
  }

  // Continue generating a truncated assistant response.
  // Keep the existing partial response visible and stream the continuation
  // into a new assistant message.
  emitContinueGeneration({ userId, conversationId, messageId }) {
    if (!this.isConnected || !conversationId || !userId || !messageId) return;

    const chatStore = useChatStore.getState();
    const chat = chatStore.chats.find(c => c.id === conversationId);
    const message = chat?.messages?.find(m => m.id === messageId || m._id === messageId);

    if (message) {
      this.pendingContinuationMessages.set(conversationId, {
        messageId,
        message: {
          ...message,
          isThinking: false,
          isTyping: false,
          isWaiting: false,
          isStreaming: false,
        },
      });
    }

    if (message) {
      chatStore.updateLocalChatMessage(conversationId, messageId, {
        isTruncated: false,
        stopReason: 'continued',
      });
    }

    chatStore.updateLocalChatDetails(conversationId, {
      isInferencing: true,
      isInferencingWaiting: true,
    });

    this._markStreamActivity(conversationId);
    this._emitSocketEvent(userId, 'continue_generation', {
      userId,
      conversationId,
      messageId,
    });
  }

  // Test connection with ping
  emitPing(userId) {
    if (this.isConnected) {
      this._emitSocketEvent(userId, 'ping', { userId });
    }
  }

  // Toggle fast mode for a conversation
  // Options: { silent: boolean } - if true, don't show toast (used when syncing after conversation creation)
  emitToggleFastMode(userId, conversationId, enabled, options = {}) {
    const { silent = false } = options;
    
    console.log('[Socket] emitToggleFastMode called:', { 
      userId, 
      conversationId, 
      enabled, 
      silent,
      isConnected: this.isConnected 
    });
    
    if (!this.isConnected) {
      console.warn('[Socket] emitToggleFastMode: Not connected to socket');
      if (!silent) {
        const { addToast } = useToastStore.getState().actions;
        addToast(
          getTranslation('socket.fastModeNotConnected'),
          'error',
          [],
          { category: 'connection' },
        );
      }
      return;
    }
    if (!conversationId || !userId) {
      console.warn('[Socket] emitToggleFastMode: Missing required params', { conversationId, userId });
      return;
    }

    console.log('[Socket] emitToggleFastMode: Emitting toggleFastMode event');
    this._emitSocketEvent(userId, 'toggleFastMode', {
      conversationId: conversationId,
      enabled: enabled,
    });
    
    // Optimistically update the UI (don't wait for server confirmation)
    const chatStore = useChatStore.getState();
    const chat = chatStore.chats.find(c => c.id === conversationId);
    console.log('[Socket] emitToggleFastMode: Chat found:', { 
      chatFound: !!chat, 
      currentFlags: chat?.flags 
    });
    
    if (chat) {
      chatStore.updateLocalChatDetails(conversationId, {
        flags: {
          ...(chat.flags || {}),
          fastMode: enabled,
        }
      });
      console.log('[Socket] emitToggleFastMode: Store updated optimistically');
    } else {
      console.warn('[Socket] emitToggleFastMode: Chat not found in store, cannot update optimistically');
    }
  }

  // Emit fast answer request (cancel preprocessing and restart with fast mode)
  emitFastAnswerNow(userId, conversationId) {
    if (!this.isConnected) {
      devWarn('Socket not connected. Cannot request fast answer');
      return;
    }
    if (!conversationId || !userId) {
      devWarn('Conversation ID and user ID are required');
      return;
    }

    devLog('[Socket] emitFastAnswerNow:', { conversationId });
    this._emitSocketEvent(userId, 'fastAnswerNow', {
      conversationId: conversationId,
    });

    // Update the message to show we're switching to fast mode
    const chatStore = useChatStore.getState();
    const chat = chatStore.chats.find(c => c.id === conversationId);
    const thinkingMessage = chat?.messages?.find(m => 
      m.role === 'assistant' && (m.isThinking || m.isWaiting)
    );
    
    if (thinkingMessage) {
      chatStore.updateLocalChatMessage(conversationId, thinkingMessage.id, {
        inferenceStatus: 'Switching to fast mode…',
        canFastAnswer: false, // Hide the button once clicked
      });
    }
  }
}

// Create a singleton instance
const socketManager = new SocketManager();

export default socketManager;
